import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CodeMirror from '@uiw/react-codemirror';
import { java } from '@codemirror/lang-java';
import { python } from '@codemirror/lang-python';
import { javascript } from '@codemirror/lang-javascript';
import { cpp } from '@codemirror/lang-cpp';
import { basicLight } from '@fsegurai/codemirror-theme-basic-light';
import { oneDark } from '@codemirror/theme-one-dark';
import ReactMarkdown from 'react-markdown';
import Joyride, { STATUS } from 'react-joyride';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import api from '../services/api';

const QuestionIDE = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Question data
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionQuestions, setSectionQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  
  // IDE states
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('javascript');

  // Get CodeMirror language extension
  const getLanguageExtension = (lang) => {
    switch (lang.toLowerCase()) {
      case 'java':
        return java();
      case 'python':
        return python();
      case 'javascript':
      case 'js':
        return javascript();
      case 'cpp':
      case 'c++':
        return cpp();
      default:
        return javascript();
    }
  };
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [useCustomInput, setUseCustomInput] = useState(false);
  
  // Theme state
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('questionIDE-theme');
    return savedTheme || 'dark';
  });

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('questionIDE-theme', theme);
  }, [theme]);

  // UI states
  const [showQuestionPanel, setShowQuestionPanel] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 640) return screenWidth;
    const editorMinWidth = 400;
    const maxWidth = screenWidth - editorMinWidth;
    return Math.min(500, Math.max(300, maxWidth * 0.6));
  });
  const [isResizing, setIsResizing] = useState(false);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 640;
      setIsMobile(mobile);
      if (mobile) {
        setLeftPanelWidth(window.innerWidth);
      } else {
        const screenWidth = window.innerWidth;
        const editorMinWidth = 400;
        const maxWidth = screenWidth - editorMinWidth;
        setLeftPanelWidth(Math.min(500, Math.max(300, maxWidth * 0.6)));
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top on mount and when question/route changes (enhanced for mobile)
  useEffect(() => {
    const scrollToTop = () => {
      // Multiple methods to ensure it works across all browsers and devices, especially mobile
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant' // Use 'instant' for immediate scroll (works better on mobile)
      });
      
      // Fallback for older browsers
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
      
      // Direct property setting (works on mobile Safari)
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // For mobile browsers that use different scroll containers
      const rootElement = document.documentElement || document.body;
      if (rootElement.scrollTop !== 0) {
        rootElement.scrollTop = 0;
      }
    };

    // Immediate scroll
    scrollToTop();

    // Also try after a small delay to catch any late-rendering content
    const timeoutId = setTimeout(() => {
      scrollToTop();
    }, 0);

    // Use requestAnimationFrame for better mobile support
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
    });

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [searchParams]); // Trigger on search params change (questionId, etc.)

  // Show mobile warning on mount if on mobile (always show, don't remember dismissal)
  useEffect(() => {
    if (isMobile && !loading) {
      // Always show the warning when Question IDE opens on mobile
      setShowMobileWarning(true);
    }
  }, [isMobile, loading]);
  
  const [activeTab, setActiveTab] = useState('problem');
  const [showSolution, setShowSolution] = useState(false);
  const [showSolutionWarning, setShowSolutionWarning] = useState(false);
  const [hasViewedSolution, setHasViewedSolution] = useState(false);
  const [isLanguageChanging, setIsLanguageChanging] = useState(false);
  
  // Test cases and submissions
  const [testCases, setTestCases] = useState([]); // Basic test cases (visible to user)
  const [hiddenTestCases, setHiddenTestCases] = useState([]); // Full test cases (for submission)
  const [submissions, setSubmissions] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [startTime, setStartTime] = useState(Date.now()); // Track when user started working on question
  
  // Code Quality
  const [showCodeQuality, setShowCodeQuality] = useState(false);
  const [codeQualityResults, setCodeQualityResults] = useState(null);
  const [isAnalyzingCode, setIsAnalyzingCode] = useState(false);
  
  // Ghost Coding
  const [isRecording, setIsRecording] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showRecordings, setShowRecordings] = useState(false);
  const [availableRecordings, setAvailableRecordings] = useState([]);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const recordingIntervalRef = useRef(null);
  const keyStrokesRef = useRef([]);
  
  // XP Info
  // eslint-disable-next-line no-unused-vars
  const [xpInfo, setXpInfo] = useState(null);
  
  // AI Features
  const [showAIHint, setShowAIHint] = useState(false);
  const [aiHint, setAIHint] = useState(null);
  const [hintLevel, setHintLevel] = useState(1);
  const [isLoadingHint, setIsLoadingHint] = useState(false);
  const [showAIExplainer, setShowAIExplainer] = useState(false);
  const [aiExplanation, setAIExplanation] = useState(null);
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  
  // Auth modals
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptFeature, setLoginPromptFeature] = useState('');
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showComplexityAnalysis, setShowComplexityAnalysis] = useState(false);
  const [complexityAnalysis, setComplexityAnalysis] = useState(null);
  
  // Mobile warning popup
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [isAnalyzingComplexity, setIsAnalyzingComplexity] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [aiLoadingMessage, setAiLoadingMessage] = useState('');
  
  // Submission Success Modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionResults, setSubmissionResults] = useState(null);
  
  // Track if user has already solved this question (for XP award)
  const [hasAlreadySolved, setHasAlreadySolved] = useState(false);
  
  // Tour state
  const [runTour, setRunTour] = useState(false);
  
  // Ref for AI menu button and dropdown
  const aiMenuButtonRef = useRef(null);
  const aiMenuDropdownRef = useRef(null);
  
  // Close AI menu when clicking outside (desktop)
  useEffect(() => {
    if (!showAIMenu) return;
    
    const handleClickOutside = (event) => {
      // Check if click is outside both the button and the dropdown
      const dropdown = aiMenuDropdownRef.current;
      const button = aiMenuButtonRef.current;
      
      if (dropdown && button) {
        const isClickInDropdown = dropdown.contains(event.target);
        const isClickInButton = button.contains(event.target);
        
        if (!isClickInDropdown && !isClickInButton) {
          setShowAIMenu(false);
        }
      }
    };
    
    // Add event listener after a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAIMenu]);
  
  // Position dropdown relative to button on desktop (using fixed positioning to escape overflow:hidden)
  const updateDropdownPosition = useCallback(() => {
    if (showAIMenu && !isMobile && aiMenuButtonRef.current && aiMenuDropdownRef.current) {
      const buttonRect = aiMenuButtonRef.current.getBoundingClientRect();
      // Use getBoundingClientRect() which gives viewport-relative coordinates (perfect for fixed positioning)
      const top = buttonRect.bottom + 8;
      const left = buttonRect.left;
      
      aiMenuDropdownRef.current.style.top = `${top}px`;
      aiMenuDropdownRef.current.style.left = `${left}px`;
      aiMenuDropdownRef.current.style.display = 'block';
      aiMenuDropdownRef.current.style.visibility = 'visible';
    }
  }, [showAIMenu, isMobile]);
  
  useEffect(() => {
    if (showAIMenu && !isMobile) {
      // Use requestAnimationFrame to ensure DOM is ready
      const frameId = requestAnimationFrame(() => {
        // Small delay to ensure portal has rendered
        setTimeout(() => {
          updateDropdownPosition();
        }, 0);
      });
      
      // Update position on scroll and resize
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        cancelAnimationFrame(frameId);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showAIMenu, isMobile, updateDropdownPosition]);
  
  const editorViewRef = useRef(null);
  const editorContainerRef = useRef(null);
  const resizeRef = useRef(null);
  const verticalResizeRef = useRef(null);
  const leftPanelRef = useRef(null);
  const editorPanelRef = useRef(null);
  const outputPanelRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState(60); // Percentage of container
  const [isVerticalResizing, setIsVerticalResizing] = useState(false);

  // Store initial state for reset functionality
  const initialEditorStateRef = useRef(null);
  // Track if we've loaded saved state for the current question
  const hasLoadedSavedStateRef = useRef(false);
  const currentQuestionIdRef = useRef(null);

  // Helper functions for state persistence (memoized with useCallback)
  // Include user ID in key to prevent cross-user state sharing
  const saveEditorState = useCallback((questionId, state) => {
    if (!questionId) return;
    try {
      // Get user ID - use current user if available
      const userId = user?.id ? user.id.toString() : 'anonymous';
      const stateKey = `question_editor_state_${questionId}_${userId}`;
      localStorage.setItem(stateKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save editor state:', error);
    }
  }, [user]);

  const loadEditorState = useCallback((questionId) => {
    if (!questionId) return null;
    try {
      // Determine user ID
      let userId = 'anonymous';
      if (user?.id) {
        userId = user.id.toString();
      }
      
      // Try loading with current user ID first
      let stateKey = `question_editor_state_${questionId}_${userId}`;
      let savedState = localStorage.getItem(stateKey);
      
      if (savedState) {
        return JSON.parse(savedState);
      }
      
      // If user is authenticated but no state found with user ID, check anonymous
      // This handles the case where state was saved before user loaded
      if (isAuthenticated() && userId !== 'anonymous') {
        stateKey = `question_editor_state_${questionId}_anonymous`;
        savedState = localStorage.getItem(stateKey);
        if (savedState) {
          // Migrate anonymous state to user-specific key
          const state = JSON.parse(savedState);
          const newStateKey = `question_editor_state_${questionId}_${userId}`;
          localStorage.setItem(newStateKey, JSON.stringify(state));
          localStorage.removeItem(stateKey); // Clean up old key
          return state;
        }
      }
    } catch (error) {
      console.error('Failed to load editor state:', error);
    }
    return null;
  }, [user, isAuthenticated]);

  const saveInitialState = useCallback((questionId, state) => {
    if (!questionId) return;
    try {
      const userId = user?.id || 'anonymous';
      const initialStateKey = `question_editor_initial_state_${questionId}_${userId}`;
      localStorage.setItem(initialStateKey, JSON.stringify(state));
      initialEditorStateRef.current = state;
    } catch (error) {
      console.error('Failed to save initial state:', error);
    }
  }, [user?.id]);

  const loadInitialState = useCallback((questionId) => {
    if (!questionId) return null;
    try {
      const userId = user?.id || 'anonymous';
      const initialStateKey = `question_editor_initial_state_${questionId}_${userId}`;
      const savedState = localStorage.getItem(initialStateKey);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error('Failed to load initial state:', error);
    }
    return null;
  }, [user?.id]);

  const clearEditorState = useCallback((questionId) => {
    if (!questionId) return;
    try {
      const userId = user?.id || 'anonymous';
      const stateKey = `question_editor_state_${questionId}_${userId}`;
      localStorage.removeItem(stateKey);
    } catch (error) {
      console.error('Failed to clear editor state:', error);
    }
  }, [user?.id]);

  // Normalize newlines - convert escaped newlines and ensure proper formatting
  const normalizeNewlines = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/\\n/g, '\n')  // Convert escaped newlines
      .replace(/\r\n/g, '\n') // Normalize Windows line endings
      .replace(/\r/g, '\n');   // Normalize Mac line endings
  };

  // Parse test cases from string format
  const parseTestCases = (testCasesString) => {
    if (!testCasesString) return [];
    
    try {
      // Don't filter out empty lines - preserve them for multi-line inputs
      const lines = testCasesString.split('\n');
      const testCases = [];
      
      // First, check for delimiter-based format (e.g., "---" or "===" between test cases)
      // This makes it unambiguous where one test case ends and another begins
      let hasDelimiter = false;
      const delimiterPattern = /^[-=]{3,}$/; // Matches "---", "====", etc.
      
      for (const line of lines) {
        if (delimiterPattern.test(line.trim())) {
          hasDelimiter = true;
          break;
        }
      }
      
      if (hasDelimiter) {
        // Parse delimiter-separated format
        // Format: 
        //   input line 1
        //   input line 2
        //   input line 3 | output line 1
        //   output line 2
        //   output line 3
        //   ---
        //   next input line 1
        //   ...
      let testCaseId = 1;
        let currentTestCase = { input: [], output: [] };
        let inOutput = false;
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const trimmed = line.trim();
          
          if (delimiterPattern.test(trimmed)) {
            // Delimiter found - save current test case and start new one
            if (currentTestCase.input.length > 0 || currentTestCase.output.length > 0) {
              const inputStr = currentTestCase.input.join('\n');
              const outputStr = currentTestCase.output.join('\n');
              if (inputStr.trim() || outputStr.trim()) {
                testCases.push({
                  id: testCaseId++,
                  input: inputStr,
                  expected: outputStr,
                  output: outputStr,
                  description: `Test Case ${testCaseId - 1}`
                });
              }
            }
            currentTestCase = { input: [], output: [] };
            inOutput = false;
            continue;
          }
          
          if (line.includes('|')) {
            // Pipe found - split input and output
            const pipeIndex = line.indexOf('|');
            const beforePipe = line.substring(0, pipeIndex);
            const afterPipe = line.substring(pipeIndex + 1);
            
            if (beforePipe.trim() || currentTestCase.input.length > 0) {
              currentTestCase.input.push(beforePipe);
            }
            if (afterPipe.trim() || afterPipe === '') {
              currentTestCase.output.push(afterPipe);
            }
            inOutput = true;
          } else if (inOutput) {
            // We're in output section
            currentTestCase.output.push(line);
          } else {
            // We're in input section
            currentTestCase.input.push(line);
          }
        }
        
        // Don't forget the last test case
        if (currentTestCase.input.length > 0 || currentTestCase.output.length > 0) {
          const inputStr = currentTestCase.input.join('\n');
          const outputStr = currentTestCase.output.join('\n');
          if (inputStr.trim() || outputStr.trim()) {
            testCases.push({
              id: testCaseId++,
              input: inputStr,
              expected: outputStr,
              output: outputStr,
              description: `Test Case ${testCaseId - 1}`
            });
          }
        }
        
        if (testCases.length > 0) {
          return testCases;
        }
      }
      
      // Otherwise, check if it's single-line format (old simple logic)
      // Single-line format: each line is "input | output", one test case per line
      let hasPipeInText = false;
      let isSingleLineFormat = true;
      
      // Check if any line contains a pipe
      for (const line of lines) {
        if (line.includes('|')) {
          hasPipeInText = true;
          // Check if this is single-line format: pipe should be on the same line as input/output
          // and there should be no input lines before the pipe line
          break;
        }
      }
      
      // If pipes exist, check if it's single-line format
      // Single-line: each line with pipe contains both input and output on that line
      // Multi-line: there are lines before/after the pipe line
      if (hasPipeInText) {
        // Check if format is single-line (each pipe line has both input and output on same line)
        // vs multi-line (input spans multiple lines before pipe, output spans multiple lines after)
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.includes('|')) {
            // If there are non-empty lines before this pipe line (and previous line doesn't have pipe),
            // it's multi-line format (input spans multiple lines)
            if (i > 0 && lines[i - 1].trim() && !lines[i - 1].includes('|')) {
              isSingleLineFormat = false;
              break;
            }
            
            // Check if there are non-empty lines after this pipe before next pipe or end
            let foundNextPipe = false;
            for (let j = i + 1; j < lines.length; j++) {
              if (lines[j].includes('|')) {
                foundNextPipe = true;
                // If there are non-empty lines between pipes, it's multi-line
                for (let k = i + 1; k < j; k++) {
                  if (lines[k].trim()) {
                    isSingleLineFormat = false;
                    break;
                  }
                }
                break;
              }
            }
            
            // If no next pipe found, check if there are non-empty lines after this pipe
            if (!foundNextPipe) {
              for (let j = i + 1; j < lines.length; j++) {
                if (lines[j].trim()) {
                  isSingleLineFormat = false;
                  break;
                }
              }
            }
            
            if (!isSingleLineFormat) break;
          }
        }
      }
      
      if (hasPipeInText && isSingleLineFormat) {
        // Old simple logic for single-line format: "input | output" per line
        let testCaseId = 1;
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.includes('|')) {
            const pipeIndex = line.indexOf('|');
            const input = line.substring(0, pipeIndex).trim();
            const output = line.substring(pipeIndex + 1).trim();
            
            if (input || output) {
            testCases.push({
              id: testCaseId++,
                input: input,
                expected: output,
                output: output,
              description: `Test Case ${testCaseId - 1}`
            });
          }
        }
      }
      
      if (testCases.length > 0) {
        return testCases;
        }
      }
      
      // Multi-line format without delimiters - use heuristic-based parsing
      if (hasPipeInText && !isSingleLineFormat) {
        // Process pipe-separated format
        // Strategy: Process in two passes
        // Pass 1: Find all pipes and collect raw input/output pairs
        // Pass 2: Determine boundaries between test cases
        
        let testCaseId = 1;
        let pipePositions = [];
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].includes('|')) {
            pipePositions.push(i);
          }
        }
        
        // For each pipe, determine input and output boundaries
        for (let pipeIdx = 0; pipeIdx < pipePositions.length; pipeIdx++) {
          const pipeLineIdx = pipePositions[pipeIdx];
          const pipeLine = lines[pipeLineIdx];
          const nextPipeLineIdx = pipeIdx < pipePositions.length - 1 ? pipePositions[pipeIdx + 1] : lines.length;
          
          // Determine input start: 
          // - For first test case: start of text (line 0)
          // - For subsequent: we'll determine this in post-processing
          // For now, collect from after previous pipe to current pipe
          // Post-processing will fix the boundary
          let inputStartIdx = 0;
          if (pipeIdx > 0) {
            const prevPipeLineIdx = pipePositions[pipeIdx - 1];
            // Start collecting from after previous pipe
            // Post-processing will determine the actual boundary
            inputStartIdx = prevPipeLineIdx + 1;
          }
          
          // Collect input lines (may include some lines that are actually previous output)
          // Post-processing will fix this
          let inputLines = [];
          for (let i = inputStartIdx; i < pipeLineIdx; i++) {
            inputLines.push(lines[i]);
          }
          
          // Add part before pipe
          const pipeIndex = pipeLine.indexOf('|');
          const beforePipe = pipeLine.substring(0, pipeIndex);
          if (beforePipe.trim() || inputLines.length > 0) {
            inputLines.push(beforePipe);
          }
          
          // For output: collect from after pipe until next pipe
          // But we need to be careful - some lines might be next test case's input
          let outputLines = [];
          const afterPipe = pipeLine.substring(pipeIndex + 1);
          outputLines.push(afterPipe);
          
          // Collect remaining output lines
          // The output should include lines until we hit what looks like
          // the start of the next test case's input
          // For now, collect all until next pipe, we'll refine later
          for (let i = pipeLineIdx + 1; i < nextPipeLineIdx; i++) {
            outputLines.push(lines[i]);
          }
          
          // If this is not the last test case, we may have collected too much in output
          // The lines right before the next pipe are likely the next test case's input
          // But we don't know how many. Let's leave it for now and see if it works
          
          const inputStr = inputLines.join('\n');
          const outputStr = outputLines.join('\n');
          
          if (inputStr.trim() || outputStr.trim()) {
            testCases.push({
              id: testCaseId++,
              input: inputStr,
              expected: outputStr,
              output: outputStr,
              description: `Test Case ${testCaseId - 1}`
            });
          }
        }
        
        // Post-process: Fix boundaries between test cases
        // When there are multiple test cases, lines between pipes need to be split
        // between previous output and current input
        if (testCases.length > 1) {
          for (let i = 1; i < testCases.length; i++) {
            const prevPipeLineIdx = pipePositions[i - 1];
            const currentPipeLineIdx = pipePositions[i];
            
            // Lines between the two pipes (excluding the pipe lines themselves)
            const linesBetween = [];
            for (let j = prevPipeLineIdx + 1; j < currentPipeLineIdx; j++) {
              linesBetween.push(lines[j]);
            }
            
            if (linesBetween.length > 0) {
              // Heuristic to find boundary:
              // 1. Count data lines in previous input (excluding dimension line if present)
              // 2. Output often has similar number of data lines
              // 3. Next input starts with a dimension-like line (e.g., "3 2", "1 4")
              
              // Count previous input data lines
              let prevInputDataLines = 0;
              const startIdx = i > 1 ? pipePositions[i - 2] + 1 : 0;
              for (let j = startIdx; j < prevPipeLineIdx; j++) {
                if (lines[j].trim()) prevInputDataLines++;
              }
              // Add the part before pipe
              const prevPipeLine = lines[prevPipeLineIdx];
              const prevBeforePipe = prevPipeLine.substring(0, prevPipeLine.indexOf('|')).trim();
              if (prevBeforePipe) prevInputDataLines++;
              
              // Look for a line that looks like dimension (2 numbers or pattern suggesting new test case)
              let boundaryIdx = linesBetween.length;
              for (let j = 0; j < linesBetween.length; j++) {
                const line = linesBetween[j].trim();
                // Check if this looks like the start of a new test case input
                // Pattern: "X Y" (two numbers) often indicates dimensions for 2D array
                // or "X" (single number) for 1D array
                if (line && (/^\d+\s+\d+$/.test(line) || /^\d+$/.test(line))) {
                  // This might be the start of next test case
                  // Check if we've collected enough output lines
                  if (j >= Math.max(1, prevInputDataLines - 1)) {
                    boundaryIdx = j;
                    break;
                  }
                }
              }
              
              // If we didn't find a clear boundary, use a default split
              // Assume output is roughly the same length as input data lines
              if (boundaryIdx === linesBetween.length && prevInputDataLines > 0) {
                boundaryIdx = Math.min(prevInputDataLines, linesBetween.length);
              }
              
              // Rebuild previous output
              const prevPipeLine2 = lines[prevPipeLineIdx];
              const prevPipeIndex = prevPipeLine2.indexOf('|');
              let newPrevOutput = [prevPipeLine2.substring(prevPipeIndex + 1)];
              for (let j = 0; j < boundaryIdx; j++) {
                newPrevOutput.push(linesBetween[j]);
              }
              
              // Rebuild current input
              let newCurrentInput = [];
              for (let j = boundaryIdx; j < linesBetween.length; j++) {
                newCurrentInput.push(linesBetween[j]);
              }
              const currentPipeLine = lines[currentPipeLineIdx];
              const currentPipeIndex = currentPipeLine.indexOf('|');
              const currentBeforePipe = currentPipeLine.substring(0, currentPipeIndex);
              if (currentBeforePipe.trim() || newCurrentInput.length > 0) {
                newCurrentInput.push(currentBeforePipe);
              }
              
              // Update test cases
              testCases[i - 1].expected = newPrevOutput.join('\n');
              testCases[i - 1].output = newPrevOutput.join('\n');
              testCases[i].input = newCurrentInput.join('\n');
            }
          }
        }
        
        if (testCases.length > 0) {
          return testCases;
        }
      }
      
      // Otherwise, try the old "Input: / Expected:" format
      let currentTestCase = null;
      let currentSection = null; // 'input' or 'expected'
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        
        if (trimmed.toLowerCase().includes('test case') && !trimmed.toLowerCase().startsWith('input:')) {
          if (currentTestCase) {
            testCases.push(currentTestCase);
          }
          currentTestCase = {
            id: testCases.length + 1,
            input: '',
            expected: '',
            output: '', // Alias for backend compatibility
            description: trimmed
          };
          currentSection = null;
        } else if (trimmed.toLowerCase().startsWith('input:')) {
          if (!currentTestCase) {
            currentTestCase = {
              id: testCases.length + 1,
              input: '',
              expected: '',
              output: '',
              description: `Test Case ${testCases.length + 1}`
            };
          }
          currentSection = 'input';
          const inputContent = trimmed.replace(/^input:\s*/i, '').trim();
          if (inputContent) {
            currentTestCase.input = inputContent;
          } else {
            currentTestCase.input = '';
          }
        } else if (trimmed.toLowerCase().startsWith('expected:') || trimmed.toLowerCase().startsWith('output:')) {
          if (!currentTestCase) {
            currentTestCase = {
              id: testCases.length + 1,
              input: '',
              expected: '',
              output: '',
              description: `Test Case ${testCases.length + 1}`
            };
          }
          currentSection = 'expected';
          const outputContent = trimmed.replace(/^(expected|output):\s*/i, '').trim();
          if (outputContent) {
            currentTestCase.expected = outputContent;
            currentTestCase.output = outputContent;
          } else {
            currentTestCase.expected = '';
            currentTestCase.output = '';
          }
        } else if (currentTestCase && currentSection) {
          // Accumulate multi-line content for current section - preserve original line (including empty lines)
          if (currentSection === 'input') {
            if (currentTestCase.input) {
              currentTestCase.input += '\n' + line;
            } else {
            currentTestCase.input = line;
            }
          } else if (currentSection === 'expected') {
            if (currentTestCase.expected) {
              currentTestCase.expected += '\n' + line;
              currentTestCase.output += '\n' + line;
            } else {
            currentTestCase.expected = line;
            currentTestCase.output = line;
            }
          }
        } else if (line && currentTestCase && !trimmed.toLowerCase().includes('test case')) {
          // No section set yet, but we have a test case - assume it's input
          if (currentTestCase.input) {
            currentTestCase.input += '\n' + line;
          } else {
            currentTestCase.input = line;
          }
        }
      }
      
      if (currentTestCase) {
        testCases.push(currentTestCase);
      }
      
      return testCases;
    } catch (error) {
      console.error('Error parsing test cases:', error);
      return [];
    }
  };

  // Parse and render examples in a structured way
  const renderExamples = (examplesText) => {
    if (!examplesText) return null;
    
    // Split by "Example" markers to find individual examples
    const exampleBlocks = examplesText.split(/(?=Example\s*\d+\s*:)/i).filter(block => block.trim());
    
    const examples = [];
    
    // If no Example markers, try to split by Input: markers
    const blocksToProcess = exampleBlocks.length > 0 ? exampleBlocks : examplesText.split(/(?=Input\s*:)/i).filter(block => block.trim());
    
    blocksToProcess.forEach(block => {
      const lines = block.split('\n');
      
      let currentExample = {
        input: '',
        output: '',
        explanation: ''
      };
      
      let currentSection = null;
      
      lines.forEach((line, lineIndex) => {
        const trimmed = line.trim();
        
        // Check for Example header
        if (trimmed.match(/^Example\s*\d+\s*:/i)) {
          // If we have a previous example, save it
          if (currentExample.input || currentExample.output) {
            examples.push({ ...currentExample });
          }
          currentExample = { input: '', output: '', explanation: '' };
          currentSection = null;
          return;
        }
        
        // Check for Input:
        if (trimmed.match(/^Input\s*:/i)) {
          currentSection = 'input';
          const content = trimmed.replace(/^Input\s*:\s*/i, '').trim();
          if (content) {
            currentExample.input = content;
          } else {
            currentExample.input = '';
          }
          return;
        }
        
        // Check for Output: or Expected:
        if (trimmed.match(/^(?:Output|Expected)\s*:/i)) {
          currentSection = 'output';
          const content = trimmed.replace(/^(?:Output|Expected)\s*:\s*/i, '').trim();
          if (content) {
            currentExample.output = content;
          } else {
            currentExample.output = '';
          }
          return;
        }
        
        // Check for Explanation:
        if (trimmed.match(/^Explanation\s*:/i)) {
          currentSection = 'explanation';
          const content = trimmed.replace(/^Explanation\s*:\s*/i, '').trim();
          if (content) {
            currentExample.explanation = content;
          } else {
            currentExample.explanation = '';
          }
          return;
        }
        
        // If we're in a section and the line is not empty, append to current section
        if (currentSection && trimmed) {
          if (currentExample[currentSection]) {
            currentExample[currentSection] += '\n' + trimmed;
          } else {
            currentExample[currentSection] = trimmed;
          }
        }
      });
      
      // Push the last example
      if (currentExample.input || currentExample.output) {
        examples.push({ ...currentExample });
      }
    });
    
    // If no structured examples found, return the text as-is
    if (examples.length === 0) {
      return <pre className="text-sm font-mono whitespace-pre-wrap">{examplesText}</pre>;
    }
    
    return (
      <div className="space-y-3">
        {examples.map((example, idx) => (
          <div key={idx} className={`rounded-lg p-3 sm:p-4 border shadow-sm ${
            theme === 'dark' 
              ? 'bg-gray-800 border-indigo-700' 
              : 'bg-white border-indigo-200'
          }`}>
            <div className={`text-xs sm:text-sm font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
            }`}>Example {idx + 1}</div>
            <div className="space-y-3 sm:space-y-4">
              {example.input && (
                <div>
                  <span className={`text-xs sm:text-sm font-semibold block mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>Input:</span>
                  <pre 
                    className="question-example-input text-xs sm:text-sm p-2 sm:p-3 rounded-lg font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words border leading-relaxed" 
                    style={{ 
                      backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb', 
                      color: theme === 'dark' ? '#e5e7eb' : '#111827', 
                      borderColor: theme === 'dark' ? '#374151' : '#d1d5db',
                    }}
                  >
                    {example.input}
                  </pre>
                </div>
              )}
              {example.output && (
                <div>
                  <span className={`text-xs sm:text-sm font-semibold block mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>Output:</span>
                  <pre 
                    className="question-example-output text-xs sm:text-sm p-2 sm:p-3 rounded-lg font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words border leading-relaxed" 
                    style={{ 
                      backgroundColor: theme === 'dark' ? '#064e3b' : '#ecfdf5', 
                      color: theme === 'dark' ? '#6ee7b7' : '#111827', 
                      borderColor: theme === 'dark' ? '#065f46' : '#86efac',
                    }}
                  >
                    {example.output}
                  </pre>
                </div>
              )}
              {example.explanation && (
                <div>
                  <span className={`text-xs sm:text-sm font-semibold block mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>Explanation:</span>
                  <div className={`text-xs sm:text-sm italic whitespace-pre-wrap leading-relaxed p-2 sm:p-3 rounded-lg border ${
                    theme === 'dark' 
                      ? 'text-gray-300 bg-gray-900 border-gray-700' 
                      : 'text-gray-700 bg-gray-50 border-gray-200'
                  }`}>
                    {example.explanation}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Load question data
  useEffect(() => {
    const loadQuestion = async () => {
      try {
        setLoading(true);
        const questionId = searchParams.get('questionId') || searchParams.get('question');
        const courseId = searchParams.get('courseId');
        const packageId = searchParams.get('packageId');
        const sectionId = searchParams.get('sectionId');
        
        // Reset saved state flag if question changed
        if (currentQuestionIdRef.current !== questionId) {
          hasLoadedSavedStateRef.current = false;
        }
        
        if (!questionId) {
          setError('No question ID provided');
          return;
        }

        // Check if question data is passed via URL parameters
        const title = searchParams.get('title');
        const problem = searchParams.get('problem');
        const solution = searchParams.get('solution');
        const initialCode = searchParams.get('initialCode');
        const sourceLanguage = searchParams.get('sourceLanguage');

        if (title && problem) {
          // Use URL parameters to create question object
          const questionData = {
            id: questionId,
            title: decodeURIComponent(title),
            description: decodeURIComponent(problem),
            problemStatement: decodeURIComponent(problem),
            solution: solution ? decodeURIComponent(solution) : null,
            solutionCode: solution ? decodeURIComponent(solution) : null,
            initialCode: (() => {
              if (initialCode) {
                const decodedCode = decodeURIComponent(initialCode);
                const sourceLang = sourceLanguage || 'javascript';
                
                // Return only the source language code (no conversion)
                return {
                  [sourceLang]: decodedCode
                };
              }
              
              // Fallback to default templates if no initial code
              return {
                javascript: getDefaultCode('javascript'),
                python: getDefaultCode('python'),
                java: getDefaultCode('java'),
                cpp: getDefaultCode('cpp'),
                c: getDefaultCode('c')
              };
            })(),
            difficulty: 'Medium',
            difficultyLevel: 'MEDIUM',
            testCases: [],
            examples: searchParams.get('examples') ? decodeURIComponent(searchParams.get('examples')) : null,
            constraints: searchParams.get('constraints') ? decodeURIComponent(searchParams.get('constraints')) : null,
            timeLimitSeconds: searchParams.get('timeLimit') ? parseInt(searchParams.get('timeLimit')) : 1000,
            memoryLimitMB: searchParams.get('memoryLimit') ? parseInt(searchParams.get('memoryLimit')) : 256,
            sourceLanguage: sourceLanguage || 'javascript'
          };
          
          setQuestion(questionData);
          setStartTime(Date.now()); // Reset start time when question loads
          
          // Set the language to match the source language from the question
          const questionLanguage = sourceLanguage || 'javascript';
          
          // Determine initial code
          const initialCodeValue = questionData.initialCode && questionData.initialCode[questionLanguage] 
            ? questionData.initialCode[questionLanguage] 
            : getDefaultCode(questionLanguage);
          
          // Create initial state
          const initialState = {
            code: initialCodeValue,
            language: questionLanguage,
            customInput: '',
            useCustomInput: false
          };
          
          // Save initial state if not already saved
          const existingInitialState = loadInitialState(questionId);
          if (!existingInitialState) {
            saveInitialState(questionId, initialState);
          }
          
          // Try to load saved state
          const savedState = loadEditorState(questionId);
          if (savedState) {
            // Restore saved state
            setCode(savedState.code || initialCodeValue);
            setLanguage(savedState.language || questionLanguage);
            setCustomInput(savedState.customInput || '');
            setUseCustomInput(savedState.useCustomInput || false);
            hasLoadedSavedStateRef.current = true;
          } else {
            // Use initial state
            setCode(initialCodeValue);
            setLanguage(questionLanguage);
            setCustomInput('');
            setUseCustomInput(false);
            hasLoadedSavedStateRef.current = false;
          }
          
          currentQuestionIdRef.current = questionId;
          setShowQuestionPanel(true);
          
          // Try to fetch section questions for navigation (for URL param questions)
          let foundSectionId = sectionId;
          
          // If we have packageId, fetch all questions from package to find which section this question belongs to
          if (packageId && !foundSectionId) {
            try {
              const packageResponse = await api.get(`/api/questions/package/${packageId}`);
              const questionsBySection = packageResponse.data || {};
              
              // Find which section contains the current question
              for (const [sectionIdKey, questions] of Object.entries(questionsBySection)) {
                if (Array.isArray(questions)) {
                  const found = questions.find(q => (q.id === parseInt(questionId)) || (q.id === questionId));
                  if (found) {
                    foundSectionId = sectionIdKey;
                    break;
                  }
                }
              }
            } catch (packageError) {
              console.error('Error fetching package questions:', packageError);
            }
          }
          
          // If we found a section ID, fetch all questions from that section
          if (foundSectionId) {
            try {
              const sectionResponse = await api.get(`/api/questions/section/${foundSectionId}`);
              const questions = sectionResponse.data || [];
              
              // Sort questions by questionOrder if available
              const sortedQuestions = [...questions].sort((a, b) => {
                const orderA = a.questionOrder || 999;
                const orderB = b.questionOrder || 999;
                if (orderA !== orderB) {
                  return orderA - orderB;
                }
                return (a.id || 0) - (b.id || 0);
              });
              setSectionQuestions(sortedQuestions);
              
              // Find current question index
              const index = sortedQuestions.findIndex(q => (q.id === parseInt(questionId)) || (q.id === questionId));
              setCurrentQuestionIndex(index >= 0 ? index : -1);
            } catch (sectionError) {
              console.error('Error fetching section questions:', sectionError);
              setSectionQuestions([]);
              setCurrentQuestionIndex(-1);
            }
          } else {
            setSectionQuestions([]);
            setCurrentQuestionIndex(-1);
          }
        } else {
          // Try to fetch from API, but if it fails, create a basic question object
          try {
            let response;
            if (courseId) {
              response = await api.get(`/api/courses/${courseId}/questions/${questionId}`);
            } else {
              response = await api.get(`/api/questions/${questionId}`);
            }

            const questionData = response.data;
            // Build initialCode object from language-specific fields
            questionData.initialCode = {
              java: questionData.initialCodeJava || questionData.initialCode, // Fallback to old field
              python: questionData.initialCodePython || '',
              javascript: questionData.initialCodeJavaScript || '',
              cpp: questionData.initialCodeCpp || '',
              c: questionData.initialCodeC || ''
            };
            setQuestion(questionData);
            
            // Set the language to match the source language from the question
            const questionLanguage = questionData.sourceLanguage || 'java';
            
            // Determine initial code
            const initialCodeValue = questionData.initialCode[questionLanguage] 
              ? questionData.initialCode[questionLanguage] 
              : getDefaultCode(questionLanguage);
            
            // Create initial state
            const initialState = {
              code: initialCodeValue,
              language: questionLanguage,
              customInput: '',
              useCustomInput: false
            };
            
            // Save initial state if not already saved
            const existingInitialState = loadInitialState(questionId);
            if (!existingInitialState) {
              saveInitialState(questionId, initialState);
            }
            
            // Try to load saved state
            const savedState = loadEditorState(questionId);
            if (savedState) {
              // Restore saved state
              setCode(savedState.code || initialCodeValue);
              setLanguage(savedState.language || questionLanguage);
              setCustomInput(savedState.customInput || '');
              setUseCustomInput(savedState.useCustomInput || false);
              hasLoadedSavedStateRef.current = true;
            } else {
              // Use initial state
              setCode(initialCodeValue);
              setLanguage(questionLanguage);
              setCustomInput('');
              setUseCustomInput(false);
              hasLoadedSavedStateRef.current = false;
            }
            
            currentQuestionIdRef.current = questionId;
            
            // Load basic test cases (visible to user for "Run Code")
            if (questionData.testCases) {
              // Check if testCases is already an array of objects
              if (Array.isArray(questionData.testCases)) {
                // Already an array - use directly, ensuring newlines are preserved
                setTestCases(questionData.testCases.map(tc => {
                  // Normalize newlines - convert escaped newlines to actual newlines
                  let input = normalizeNewlines(tc.input || '');
                  let expected = normalizeNewlines(tc.expected || tc.output || '');
                  let output = normalizeNewlines(tc.output || tc.expected || '');
                  
                  return {
                    id: tc.id || Date.now() + Math.random(),
                    input: input,
                    expected: expected,
                    output: output,
                    description: tc.description || `Test Case ${tc.id || ''}`
                  };
                }));
              } else {
                // It's a string - parse it
              const parsedTestCases = parseTestCases(questionData.testCases);
              setTestCases(parsedTestCases);
              }
            }
            
            // Load hidden test cases (for "Submit" - includes basic + hidden)
            if (questionData.hiddenTestCases) {
              const parsedHiddenTestCases = parseTestCases(questionData.hiddenTestCases);
              setHiddenTestCases(parsedHiddenTestCases);
            } else {
              // If no hidden test cases, use basic test cases for submission too
              if (questionData.testCases) {
                const parsedTestCases = parseTestCases(questionData.testCases);
                setHiddenTestCases(parsedTestCases);
              }
            }
            
            setShowQuestionPanel(true);
            
            // Try to fetch section questions for navigation
            // Strategy: If we have packageId, fetch all questions from package and find the section
            // If we have sectionId in URL, use it directly
            // If we have courseId, try to get section from course questions
            let foundSectionId = sectionId;
            
            // If we have packageId, fetch all questions from package to find which section this question belongs to
            if (packageId && !foundSectionId) {
              try {
                const packageResponse = await api.get(`/api/questions/package/${packageId}`);
                const questionsBySection = packageResponse.data || {};
                
                // Find which section contains the current question
                for (const [sectionIdKey, questions] of Object.entries(questionsBySection)) {
                  if (Array.isArray(questions)) {
                    const found = questions.find(q => (q.id === parseInt(questionId)) || (q.id === questionId));
                    if (found) {
                      foundSectionId = sectionIdKey;
                      break;
                    }
                  }
                }
              } catch (packageError) {
                console.error('Error fetching package questions:', packageError);
              }
            }
            
            // If we found a section ID, fetch all questions from that section
            if (foundSectionId) {
              try {
                const sectionResponse = await api.get(`/api/questions/section/${foundSectionId}`);
                const questions = sectionResponse.data || [];
                
                // Sort questions by questionOrder if available
                const sortedQuestions = [...questions].sort((a, b) => {
                  const orderA = a.questionOrder || 999;
                  const orderB = b.questionOrder || 999;
                  if (orderA !== orderB) {
                    return orderA - orderB;
                  }
                  return (a.id || 0) - (b.id || 0);
                });
                setSectionQuestions(sortedQuestions);
                
                // Find current question index
                const index = sortedQuestions.findIndex(q => (q.id === parseInt(questionId)) || (q.id === questionId));
                setCurrentQuestionIndex(index >= 0 ? index : -1);
              } catch (sectionError) {
                console.error('Error fetching section questions:', sectionError);
                setSectionQuestions([]);
                setCurrentQuestionIndex(-1);
              }
            } else {
              setSectionQuestions([]);
              setCurrentQuestionIndex(-1);
            }
          } catch (apiError) {
//             // Create a basic question object if API fails
            const basicQuestion = {
              id: questionId,
              title: `Question ${questionId}`,
              description: 'This question is not available. Please check the question ID.',
              problemStatement: 'This question is not available. Please check the question ID.',
              solution: null,
              solutionCode: null,
              initialCode: {
                javascript: getDefaultCode('javascript'),
                python: getDefaultCode('python'),
                java: getDefaultCode('java'),
                cpp: getDefaultCode('cpp'),
                c: getDefaultCode('c')
              },
              difficulty: 'Medium',
              difficultyLevel: 'MEDIUM',
              testCases: [],
              examples: null,
              constraints: null,
              timeLimitSeconds: 1000,
              memoryLimitMB: 256,
              sourceLanguage: 'javascript'
            };
            
            setQuestion(basicQuestion);
            setCode(getDefaultCode(language));
            setShowQuestionPanel(true);
          }
        }
      } catch (err) {
        console.error('Error loading question:', err);
        setError('Failed to load question');
      } finally {
        setLoading(false);
      }
    };

    loadQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch XP info when question changes
  useEffect(() => {
    const fetchXpInfo = async () => {
      if (!question) {
        return;
      }

      if (!question.difficultyLevel) {
        // If difficultyLevel is not set, try to use difficulty field
        if (!question.difficulty) {
          return;
        }
      }

      const difficulty = question.difficultyLevel || question.difficulty || 'MEDIUM';
      try {
        const response = await api.get(`/api/gamification/xp-info?difficulty=${difficulty}`, {
          skipAuthRedirect: true
        });
        setXpInfo(response.data);
      } catch (error) {
        console.error('❌ Failed to fetch XP info:', error);
      }
    };

    fetchXpInfo();
  }, [question]);

  // Load submissions when question changes
  useEffect(() => {
    const loadSubmissions = async () => {
      if (!question?.id || !isAuthenticated()) {
        return;
      }

      try {
        const response = await api.get(`/api/submissions?questionId=${question.id}`, {
          skipAuthRedirect: true
        });
        if (response.data.success && response.data.submissions) {
          // Map backend submissions to frontend format
          const mappedSubmissions = response.data.submissions.map(sub => ({
            id: sub.id,
            code: sub.code,
            language: sub.language,
            status: sub.status,
            passedTests: sub.passedTests,
            totalTests: sub.totalTests,
            executionTime: sub.executionTime,
            memoryUsed: sub.memoryUsed,
            timestamp: sub.timestamp,
            testResults: sub.testResults
          }));
          setSubmissions(mappedSubmissions);
          
          // Check if user has already solved this question (any ACCEPTED submission exists)
          const hasAcceptedSubmission = mappedSubmissions.some(sub => sub.status === 'ACCEPTED');
          setHasAlreadySolved(hasAcceptedSubmission);
        }
      } catch (error) {
        console.error('❌ Failed to load submissions:', error);
        // Don't show error to user, just fail silently
      }
    };

    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  // Capture CodeMirror view reference when editor is ready
  useEffect(() => {
    if (editorContainerRef.current) {
      const findView = () => {
        const cmEditor = editorContainerRef.current?.querySelector('.cm-editor');
        if (cmEditor) {
          // @uiw/react-codemirror stores the view in the editor element
          // Try to access it via the internal structure
          const view = cmEditor._cm_view || cmEditor.cmView || 
            (cmEditor.querySelector('.cm-scroller')?._cm_view);
          if (view) {
            editorViewRef.current = view;
          }
        }
      };
      
      // Try immediately and also after a short delay
      findView();
      const timeout = setTimeout(findView, 100);
      return () => clearTimeout(timeout);
    }
  }, [code, language, question?.id]);

  // Check if user has viewed solution for this question
  useEffect(() => {
    const checkSolutionView = async () => {
      if (!question?.id || !isAuthenticated()) {
        return;
      }

      try {
        const response = await api.get(`/api/ide/solution-view/${question.id}`, {
          skipAuthRedirect: true
        });
        if (response.data.success) {
          setHasViewedSolution(response.data.hasViewed);
        }
      } catch (error) {
        console.error('Failed to check solution view status:', error);
      }
    };

    checkSolutionView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  // Clear editor state when user logs out or authentication changes
  useEffect(() => {
    const questionId = searchParams.get('questionId') || searchParams.get('question');
    if (!questionId) return;
    
    // If user is not authenticated, clear any saved state for anonymous users
    if (!isAuthenticated()) {
      // Clear anonymous state
      try {
        const anonymousStateKey = `question_editor_state_${questionId}_anonymous`;
        localStorage.removeItem(anonymousStateKey);
        // Reset to initial state
        const initialState = loadInitialState(questionId);
        if (initialState) {
          setCode(initialState.code || '');
          setLanguage(initialState.language || 'javascript');
          setCustomInput(initialState.customInput || '');
          setUseCustomInput(initialState.useCustomInput || false);
        }
        hasLoadedSavedStateRef.current = false;
      } catch (error) {
        console.error('Failed to clear anonymous state:', error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, question?.id, searchParams]);

  // Reload state when user object becomes available (in case it wasn't loaded when question loaded)
  useEffect(() => {
    if (!question?.id || !user?.id) return;
    
    const questionId = searchParams.get('questionId') || searchParams.get('question');
    if (!questionId) return;
    
    // If we haven't loaded saved state yet, try loading it now that user is available
    if (!hasLoadedSavedStateRef.current) {
      const savedState = loadEditorState(questionId);
      if (savedState && savedState.code) {
        // Use setTimeout to ensure this runs after other useEffects
        setTimeout(() => {
          setCode(savedState.code);
          setLanguage(savedState.language || language);
          setCustomInput(savedState.customInput || '');
          setUseCustomInput(savedState.useCustomInput || false);
          hasLoadedSavedStateRef.current = true;
        }, 50);
      }
    }
  }, [user?.id, question?.id, searchParams, loadEditorState, language]);

  // Save editor state to localStorage whenever it changes
  useEffect(() => {
    if (!question?.id) {
      return;
    }

    const questionId = searchParams.get('questionId') || searchParams.get('question');
    if (!questionId) {
      return;
    }

    // Save current state (works for both authenticated and anonymous users)
    const currentState = {
      code: code || '',
      language: language,
      customInput: customInput || '',
      useCustomInput: useCustomInput || false
    };
    
    saveEditorState(questionId, currentState);
  }, [code, language, customInput, useCustomInput, question?.id, searchParams, saveEditorState, user]);

  // Close AI menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAIMenu && !event.target.closest('.ai-tools-dropdown')) {
        setShowAIMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAIMenu]);

  // Prevent body scroll when success modal is open
  useEffect(() => {
    if (showSuccessModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showSuccessModal]);

  // Handle resize functionality - Smooth horizontal resize using direct DOM manipulation
  useEffect(() => {
    let rafId = null;
    
    const handleMouseMove = (e) => {
      if (!isResizing || !leftPanelRef.current) return;
      
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        const newWidth = e.clientX;
        const screenWidth = window.innerWidth;
        const minWidth = 300;
        const editorMinWidth = 400;
        const maxWidth = screenWidth - editorMinWidth;
        
        let clampedWidth = newWidth;
        if (newWidth < minWidth) {
          clampedWidth = minWidth;
        } else if (newWidth > maxWidth) {
          clampedWidth = maxWidth;
        }
        
        // Direct DOM manipulation for smooth resize - no React re-render
        // Batch DOM writes for better performance
        if (leftPanelRef.current) {
          // Update width directly - browser will batch this efficiently
          leftPanelRef.current.style.width = `${clampedWidth}px`;
        }
      });
    };

    const handleMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      
      // Sync state with actual DOM width after resize is complete
      if (leftPanelRef.current) {
        const finalWidth = leftPanelRef.current.offsetWidth;
        setLeftPanelWidth(finalWidth);
      }
      
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };

    const handleWindowResize = () => {
      const screenWidth = window.innerWidth;
      const editorMinWidth = 400; // Minimum width for editor
      const maxWidth = screenWidth - editorMinWidth; // Ensure editor has minimum space
      if (leftPanelWidth > maxWidth) {
        setLeftPanelWidth(maxWidth);
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp, { once: true });
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.pointerEvents = 'none';
      
      // Disable transitions and pointer events on panels during resize for maximum smoothness
      if (leftPanelRef.current) {
        leftPanelRef.current.style.transition = 'none';
        leftPanelRef.current.style.pointerEvents = 'none';
        leftPanelRef.current.style.contain = 'layout style paint';
      }
      
      // Re-enable pointer events on the resize handle itself
      if (resizeRef.current) {
        resizeRef.current.style.pointerEvents = 'auto';
      }
    } else {
      // Re-enable transitions and pointer events when not resizing
      if (leftPanelRef.current) {
        leftPanelRef.current.style.transition = '';
        leftPanelRef.current.style.pointerEvents = '';
        leftPanelRef.current.style.contain = '';
      }
    }

    window.addEventListener('resize', handleWindowResize);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isResizing, leftPanelWidth]);

  // Handle vertical resize functionality (editor vs output) - Direct DOM manipulation
  useEffect(() => {
    let rafId = null;
    
    const handleVerticalMouseMove = (e) => {
      if (!isVerticalResizing || !verticalResizeRef.current || !editorPanelRef.current || !outputPanelRef.current) return;
      
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        const container = verticalResizeRef.current.parentElement;
        if (!container) return;
        
        const containerRect = container.getBoundingClientRect();
        const containerHeight = containerRect.height;
        const mouseY = e.clientY - containerRect.top;
        
        // Calculate new editor height as percentage
        const newHeightPercent = (mouseY / containerHeight) * 100;
        
        // Set constraints: editor min 30%, max 80%
        const minEditorHeight = 30;
        const maxEditorHeight = 80;
        
        let clampedHeight = newHeightPercent;
        if (newHeightPercent < minEditorHeight) {
          clampedHeight = minEditorHeight;
        } else if (newHeightPercent > maxEditorHeight) {
          clampedHeight = maxEditorHeight;
        }
        
        // Direct DOM manipulation for smooth resize - no React re-render
        // Batch all DOM writes together
        if (editorPanelRef.current && outputPanelRef.current) {
          // Use CSS custom properties and direct style updates
          editorPanelRef.current.style.setProperty('--editor-height', `${clampedHeight}%`);
          editorPanelRef.current.style.height = `${clampedHeight}%`;
          editorPanelRef.current.style.transform = 'translateZ(0)';
          
          outputPanelRef.current.style.setProperty('--output-height', `${100 - clampedHeight}%`);
          outputPanelRef.current.style.height = `${100 - clampedHeight}%`;
          outputPanelRef.current.style.transform = 'translateZ(0)';
        }
      });
    };

    const handleVerticalMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      
      // Sync state with actual DOM height after resize is complete
      if (editorPanelRef.current && verticalResizeRef.current) {
        const container = verticalResizeRef.current.parentElement;
        if (container) {
          const containerHeight = container.offsetHeight;
          const editorHeightPx = editorPanelRef.current.offsetHeight;
          const finalHeightPercent = (editorHeightPx / containerHeight) * 100;
          setEditorHeight(finalHeightPercent);
        }
      }
      
      setIsVerticalResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };

    if (isVerticalResizing) {
      document.addEventListener('mousemove', handleVerticalMouseMove, { passive: true });
      document.addEventListener('mouseup', handleVerticalMouseUp, { once: true });
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.body.style.pointerEvents = 'none';
      
      // Disable transitions and pointer events on panels during resize for maximum smoothness
      if (editorPanelRef.current) {
        editorPanelRef.current.style.transition = 'none';
        editorPanelRef.current.style.pointerEvents = 'none';
        editorPanelRef.current.style.contain = 'layout style paint';
      }
      if (outputPanelRef.current) {
        outputPanelRef.current.style.transition = 'none';
        outputPanelRef.current.style.pointerEvents = 'none';
        outputPanelRef.current.style.contain = 'layout style paint';
      }
      
      // Re-enable pointer events on the resize handle itself
      if (verticalResizeRef.current) {
        verticalResizeRef.current.style.pointerEvents = 'auto';
      }
    } else {
      // Re-enable transitions and pointer events when not resizing
      if (editorPanelRef.current) {
        editorPanelRef.current.style.transition = '';
        editorPanelRef.current.style.pointerEvents = '';
        editorPanelRef.current.style.contain = '';
      }
      if (outputPanelRef.current) {
        outputPanelRef.current.style.transition = '';
        outputPanelRef.current.style.pointerEvents = '';
        outputPanelRef.current.style.contain = '';
      }
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', handleVerticalMouseMove);
      document.removeEventListener('mouseup', handleVerticalMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isVerticalResizing]);

  // Handle language change when question data changes (only when question changes, not language)
  // But don't overwrite if we've loaded saved state for this question
  useEffect(() => {
    // Skip if we've loaded saved state for this question (to preserve user's code)
    if (hasLoadedSavedStateRef.current && currentQuestionIdRef.current === question?.id) {
      return;
    }
    
    // Also skip if user is authenticated - let the state loading handle it
    if (isAuthenticated() && user?.id && question?.id) {
      const questionId = searchParams.get('questionId') || searchParams.get('question');
      if (questionId) {
        // Check if there's saved state - if so, don't overwrite
        const savedState = loadEditorState(questionId);
        if (savedState && savedState.code) {
          return;
        }
      }
    }
    
    if (question && question.initialCode && !isLanguageChanging) {
      if (question.initialCode[language]) {
        setCode(question.initialCode[language]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, language, isLanguageChanging]);

  const getDefaultCode = (lang) => {
    // Get question title for context
    const questionTitle = question?.title || 'Problem';
    
    const templates = {
      javascript: `// JavaScript solution for ${questionTitle}
class Solution {
    isValid(s) {
        // Write your code here
        
        return false;
    }
}

// DO NOT MODIFY - Test runner
const readline = require('readline');
const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
});

rl.on('line', (input) => {
    const solution = new Solution();
//     ));
    rl.close();
});`,
      python: `# Python solution for ${questionTitle}
class Solution:
    def is_valid(self, s):
        # Write your code here
        
        return False

# DO NOT MODIFY - Test runner
if __name__ == "__main__":
    import sys
    input_data = sys.stdin.readline().strip()
    solution = Solution()
    print(solution.is_valid(input_data))`,
      java: `import java.util.*;

public class Solution {
    public boolean isValid(String s) {
        // Write your code here
        
        return false;
    }
    
    // DO NOT MODIFY - Test runner
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        String input = scanner.nextLine().trim();
        Solution solution = new Solution();
        System.out.println(solution.isValid(input));
        scanner.close();
    }
}`,
      cpp: `#include <iostream>
#include <string>
using namespace std;

class Solution {
public:
    bool isValid(string s) {
        // Write your code here
        
        return false;
    }
};

// DO NOT MODIFY - Test runner
int main() {
    string input;
    getline(cin, input);
    Solution solution;
    cout << (solution.isValid(input) ? "true" : "false") << endl;
    return 0;
}`,
      c: `#include <stdio.h>
#include <string.h>
#include <stdbool.h>

bool isValid(char* s) {
    // Write your code here
    
    return false;
}

// DO NOT MODIFY - Test runner
int main() {
    char input[10000];
    fgets(input, sizeof(input), stdin);
    // Remove newline
    input[strcspn(input, "\\n")] = 0;
    printf("%s\\n", isValid(input) ? "true" : "false");
    return 0;
}`
    };
    return templates[lang] || templates.javascript;
  };

  const handleLanguageChange = (newLanguage) => {
    // Set flag to prevent useEffect interference
    setIsLanguageChanging(true);
    
    // Update language first
    setLanguage(newLanguage);
    
    // Use setTimeout to ensure state is updated
    setTimeout(() => {
      // Check if we have initial code for the new language
      if (question && question.initialCode && question.initialCode[newLanguage]) {
        setCode(question.initialCode[newLanguage]);
      } else {
        setCode(getDefaultCode(newLanguage));
      }
      
      // Reset flag after processing
      setIsLanguageChanging(false);
    }, 0);
  };

  // eslint-disable-next-line no-unused-vars
  const extractFunctionInfo = (code, language) => {
    const info = {
      functionName: 'solution',
      parameters: [],
      returnType: 'void',
      body: '// Your code here\n        return;'
    };

    try {
      // Generic patterns that work for most languages
      const patterns = {
        // Java: public int[] methodName(params) { body }
        java: /public\s+(\S+)\s+(\w+)\s*\(([^)]*)\)\s*\{([\s\S]*?)\n\s*\}/,
        // JavaScript: function methodName(params) { body }
        javascript: /function\s+(\w+)\s*\(([^)]*)\)\s*\{([\s\S]*?)\n\s*\}/,
        // Python: def methodName(params): body
        python: /def\s+(\w+)\s*\(([^)]*)\)\s*:\s*([\s\S]*?)(?=\n\s*def|\n\s*class|$)/,
        // C++: returnType methodName(params) { body }
        cpp: /(\S+)\s+(\w+)\s*\(([^)]*)\)\s*\{([\s\S]*?)\n\s*\}/,
        // C: returnType methodName(params) { body }
        c: /(\S+)\s+(\w+)\s*\(([^)]*)\)\s*\{([\s\S]*?)\n\s*\}/
      };

      const pattern = patterns[language];
      if (pattern) {
        const match = code.match(pattern);
        if (match) {
          if (language === 'python') {
            // Python has different capture groups
            info.functionName = match[1];
            info.parameters = match[2].split(',').map(p => p.trim()).filter(p => p);
            info.body = match[3].trim();
          } else {
            // Other languages
            info.returnType = match[1];
            info.functionName = match[2];
            info.parameters = match[3].split(',').map(p => p.trim()).filter(p => p);
            info.body = match[4].trim();
          }
        } else {
          // Fallback: try to extract just function signature
          const sigPatterns = {
            java: /public\s+(\S+)\s+(\w+)\s*\(([^)]*)\)/,
            javascript: /function\s+(\w+)\s*\(([^)]*)\)/,
            python: /def\s+(\w+)\s*\(([^)]*)\)/,
            cpp: /(\S+)\s+(\w+)\s*\(([^)]*)\)/,
            c: /(\S+)\s+(\w+)\s*\(([^)]*)\)/
          };
          
          const sigMatch = code.match(sigPatterns[language]);
          if (sigMatch) {
            if (language === 'python') {
              info.functionName = sigMatch[1];
              info.parameters = sigMatch[2].split(',').map(p => p.trim()).filter(p => p);
            } else {
              info.returnType = sigMatch[1];
              info.functionName = sigMatch[2];
              info.parameters = sigMatch[3].split(',').map(p => p.trim()).filter(p => p);
            }
            
            // Try to extract body separately
            const bodyMatch = code.match(/\{\s*([\s\S]*?)\s*\}/);
            if (bodyMatch) {
              info.body = bodyMatch[1].trim();
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting function info:', error);
    }

    return info;
  };

  // Generic code generation for any language
  // eslint-disable-next-line no-unused-vars
  const generateCodeForLanguage = (functionInfo, language) => {
    const { functionName, parameters, returnType, body } = functionInfo;
    
    // Clean parameters by removing type declarations
    const cleanParams = parameters.map(param => {
      // Remove type declarations (e.g., "int[] nums" -> "nums", "String s" -> "s")
      return param.replace(/^\w+(?:\[\]|\*)?\s+/, '');
    });
    
    // Generic syntax conversion
    const convertSyntax = (code, fromLang, toLang) => {
      let converted = code;
      
      // Common conversions
      const conversions = {
        // Data structures
        'Map<Integer,\\s*Integer>': toLang === 'javascript' ? 'Map' : toLang === 'python' ? '{}' : 'Map<Integer, Integer>',
        'new\\s+HashMap<>()': toLang === 'javascript' ? 'new Map()' : toLang === 'python' ? '{}' : 'new HashMap<>()',
        'HashMap': toLang === 'javascript' ? 'Map' : toLang === 'python' ? 'dict' : 'HashMap',
        
        // Method calls
        '\\.containsKey\\(': toLang === 'javascript' ? '.has(' : toLang === 'python' ? ' in ' : '.containsKey(',
        '\\.put\\(': toLang === 'javascript' ? '.set(' : toLang === 'python' ? '[' : '.put(',
        '\\.get\\(': toLang === 'javascript' ? '.get(' : toLang === 'python' ? '.get(' : '.get(',
        
        // Array/List syntax
        'new\\s+int\\[\\]\\s*\\{\\s*([^}]+)\\s*\\}': toLang === 'javascript' ? '[$1]' : toLang === 'python' ? '[$1]' : 'new int[]{$1}',
        'new\\s+int\\[\\]\\(\\)': toLang === 'javascript' ? '[]' : toLang === 'python' ? '[]' : 'new int[]()',
        '\\{\\s*': toLang === 'python' ? '[' : '{',
        '\\s*\\}': toLang === 'python' ? ']' : '}',
        
        // Return statements
        'return\\s+new\\s+int\\[\\]\\s*\\{\\s*([^}]+)\\s*\\}': toLang === 'javascript' ? 'return [$1]' : toLang === 'python' ? 'return [$1]' : 'return new int[]{$1}',
        'return\\s+new\\s+int\\[\\]\\(\\)': toLang === 'javascript' ? 'return []' : toLang === 'python' ? 'return []' : 'return new int[]()',
        
        // Variable declarations
        'int\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'int ',
        'String\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'String ',
        'boolean\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'boolean ',
        'double\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'double ',
        'float\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'float ',
        'long\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'long ',
        'char\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'char ',
        'byte\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'byte ',
        'short\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'short ',
        
        // Array types
        'int\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'int[] ',
        'String\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'String[] ',
        'boolean\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'boolean[] ',
        'double\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'double[] ',
        'float\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'float[] ',
        'long\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'long[] ',
        'char\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'char[] ',
        'byte\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'byte[] ',
        'short\\[\\]\\s+': toLang === 'javascript' ? '' : toLang === 'python' ? '' : 'short[] '
      };
      
      // Apply conversions
      Object.entries(conversions).forEach(([pattern, replacement]) => {
        const regex = new RegExp(pattern, 'g');
        converted = converted.replace(regex, replacement);
      });
      
      return converted;
    };
    
    // Convert the body syntax
    const convertedBody = convertSyntax(body, 'java', language);
    
    // Generate language-specific code
    switch (language) {
      case 'javascript':
        return `function ${functionName}(${cleanParams.join(', ')}) {
    ${convertedBody.split('\n').map(line => '    ' + line).join('\n')}
}`;
      
      case 'python':
        return `def ${functionName}(${cleanParams.join(', ')}):
    ${convertedBody.split('\n').map(line => '    ' + line).join('\n')}`;
      
      case 'java':
        const javaReturnType = convertReturnType(returnType, 'java');
        return `public class Solution {
    public ${javaReturnType} ${functionName}(${parameters.join(', ')}) {
        ${body.split('\n').map(line => '        ' + line).join('\n')}
    }
}`;
      
      case 'cpp':
        const cppReturnType = convertReturnType(returnType, 'cpp');
        return `#include <vector>
#include <unordered_map>
using namespace std;

class Solution {
public:
    ${cppReturnType} ${functionName}(${parameters.join(', ')}) {
        ${body.split('\n').map(line => '        ' + line).join('\n')}
    }
};`;
      
      case 'c':
        const cReturnType = convertReturnType(returnType, 'c');
        return `#include <stdio.h>
#include <stdlib.h>

${cReturnType} ${functionName}(${parameters.join(', ')}) {
    ${body.split('\n').map(line => '    ' + line).join('\n')}
}`;
      
      default:
        return getDefaultCode(language);
    }
  };

  // Generic return type conversion
  const convertReturnType = (returnType, targetLang) => {
    if (!returnType || returnType === 'void') return 'void';
    
    // Extract base type (remove array indicators)
    const baseType = returnType.replace(/\[\]|\*/, '').trim();
    
    // Type mapping
    const typeMap = {
      'int': {
        java: 'int',
        javascript: '', // No type declarations in JS
        python: '', // No type declarations in Python
        cpp: 'int',
        c: 'int'
      },
      'String': {
        java: 'String',
        javascript: '',
        python: '',
        cpp: 'string',
        c: 'char*'
      },
      'boolean': {
        java: 'boolean',
        javascript: '',
        python: '',
        cpp: 'bool',
        c: 'int'
      },
      'double': {
        java: 'double',
        javascript: '',
        python: '',
        cpp: 'double',
        c: 'double'
      },
      'float': {
        java: 'float',
        javascript: '',
        python: '',
        cpp: 'float',
        c: 'float'
      },
      'long': {
        java: 'long',
        javascript: '',
        python: '',
        cpp: 'long long',
        c: 'long long'
      },
      'char': {
        java: 'char',
        javascript: '',
        python: '',
        cpp: 'char',
        c: 'char'
      }
    };
    
    // Get the converted base type
    const convertedBaseType = typeMap[baseType]?.[targetLang] || baseType;
    
    // Add array indicators based on target language
    if (returnType.includes('[]') || returnType.includes('*')) {
      switch (targetLang) {
        case 'java':
          return convertedBaseType + '[]';
        case 'javascript':
        case 'python':
          return ''; // No type declarations
        case 'cpp':
          return `vector<${convertedBaseType}>`;
        case 'c':
          return convertedBaseType + '*';
        default:
          return convertedBaseType;
      }
    }
    
    return convertedBaseType;
  };

  const runCode = async () => {
    // Check authentication
    if (!isAuthenticated()) {
      setShowAuthModal(true);
      return;
    }
    
    if (!code.trim()) {
      setOutput('⚠️ Please write some code first');
      return;
    }
    
    setIsRunning(true);
    if (useCustomInput) {
      setOutput(`🚀 Running with custom input: ${customInput || '(empty)'}\n`);
    } else {
      setOutput('🚀 Running code...\n');
    }
    setTestResults([]);
    
    try {
      // Use custom input if enabled, otherwise use test cases
      const casesToRun = useCustomInput ? [
        { input: customInput || '', output: '' }
      ] : (testCases.length > 0 ? testCases : [
        { input: customInput || '', output: '' }
      ]);
      
      // Call backend to execute code against test cases
      const response = await api.post(
        '/api/ide/execute-question',
        {
          code: code,
          language: language,
          questionId: question?.id,
          startTime: startTime,
          testCases: casesToRun.map(tc => ({
            input: tc.input,
            output: tc.output || tc.expected || ''
          }))
        },
        { skipAuthRedirect: true }
      );
      
      if (response.data.success) {
        const results = response.data.results;
        const passed = response.data.passed;
        const total = response.data.total;
        
        // Update test results for UI display
        setTestResults(results);
        
        // Build output message
        let outputMsg = `✅ Execution completed!\n\n`;
        outputMsg += `📊 Results: ${passed}/${total} test cases passed\n\n`;
        
        results.forEach((result, index) => {
          outputMsg += `Test Case ${index + 1}:\n`;
          outputMsg += `Input: ${result.input}\n`;
          outputMsg += `Expected: ${result.expected}\n`;
          outputMsg += `Actual: ${result.actual}\n`;
          
          if (result.error) {
            outputMsg += `❌ ERROR: ${result.error}\n\n`;
          } else if (result.passed) {
            outputMsg += `✅ PASSED\n\n`;
          } else {
            outputMsg += `❌ FAILED\n\n`;
          }
        });
        
        if (response.data.allPassed) {
          outputMsg += `\n🎉 All test cases passed! Great job!`;
          
          // Show XP award if available
          if (response.data.xpAwarded) {
            outputMsg += `\n\n⭐ XP AWARDED! ⭐`;
            outputMsg += `\nLevel: ${response.data.level}`;
            outputMsg += `\nTotal XP: ${response.data.experiencePoints}`;
            outputMsg += `\nXP to next level: ${response.data.xpToNextLevel}`;
            outputMsg += `\nProgress: ${Math.round(response.data.levelProgress)}%`;
          }
        } else {
          outputMsg += `\n💡 ${total - passed} test case(s) failed. Keep trying!`;
        }
        
        setOutput(outputMsg);
      } else {
        setOutput(`❌ Error: ${response.data.error || 'Failed to execute code'}`);
      }
    } catch (err) {
      console.error('Execution error:', err);
      setOutput(`❌ Error: ${err.response?.data?.error || err.message || 'Failed to connect to backend'}`);
    } finally {
      setIsRunning(false);
    }
  };

  // All code execution is now handled by the backend via runCode()
  // Old client-side execution functions have been removed for security and accuracy

  const submitCode = async () => {
    // Check authentication
    if (!isAuthenticated()) {
      setShowAuthModal(true);
      return;
    }
    
    if (!code.trim()) {
      setOutput('⚠️ Please write some code first');
      return;
    }
    
    // Use hidden test cases for submission (includes basic + hidden)
    const casesToSubmit = hiddenTestCases.length > 0 ? hiddenTestCases : testCases;
    
    if (casesToSubmit.length === 0) {
      setOutput('⚠️ No test cases available for this question');
      return;
    }
    
    setIsRunning(true);
    setOutput(`🚀 Submitting your solution...\n`);
    
    try {
      // Execute code against ALL test cases (basic + hidden) on backend
      const response = await api.post(
        '/api/ide/execute-question',
        {
          code: code,
          language: language,
          questionId: question?.id,
          startTime: startTime,
          testCases: casesToSubmit.map(tc => ({
            input: tc.input,
            output: tc.output || tc.expected || ''
          }))
        },
        { skipAuthRedirect: true }
      );
      
      if (response.data.success) {
        const results = response.data.results;
        const passedCount = response.data.passed;
        const totalCount = response.data.total;
        const allPassed = response.data.allPassed;
        
        // Create submission object
        const submission = {
          questionId: question?.id,
          code: code,
          language: language,
          status: allPassed ? 'ACCEPTED' : 'WRONG_ANSWER',
          passedTests: passedCount,
          totalTests: totalCount,
          executionTime: Math.floor(Math.random() * 100) + 10, // Will be real when backend tracks it
          memoryUsed: Math.floor(Math.random() * 50) + 10, // Will be real when backend tracks it
          testResults: results // Include test results
        };
        
        // Save submission to backend
        try {
          const saveResponse = await api.post('/api/submissions', submission);
          
          // Add to local submissions list with the saved submission ID
          if (saveResponse.data.success) {
            const submissionWithId = {
              ...submission,
              id: saveResponse.data.submissionId,
              timestamp: new Date().toISOString()
            };
            setSubmissions(prev => [submissionWithId, ...prev]);
          }
        } catch (saveError) {
          console.error('Failed to save submission:', saveError);
          // Still add to local list even if save fails
          const submissionWithResults = {
            ...submission,
            id: Date.now(),
            timestamp: new Date().toISOString()
          };
          setSubmissions(prev => [submissionWithResults, ...prev]);
        }
        
        // Update test results display
        setTestResults(results);
        
        // If all tests passed, show success modal
        if (allPassed) {
          // Clear saved editor state from localStorage on successful submission
          const questionId = searchParams.get('questionId') || searchParams.get('question');
          if (questionId) {
            clearEditorState(questionId);
          }
          
          // Only show XP if this is the first successful submission
          const shouldShowXP = response.data.xpAwarded && !hasAlreadySolved;
          
          // Prepare submission results for modal
          setSubmissionResults({
            status: 'ACCEPTED',
            passedCount: passedCount,
            totalCount: totalCount,
            executionTime: submission.executionTime,
            memoryUsed: submission.memoryUsed,
            xpAwarded: shouldShowXP,
            xpSkippedReason: response.data.xpSkippedReason || null,
            level: response.data.level,
            experiencePoints: response.data.experiencePoints,
            xpToNextLevel: response.data.xpToNextLevel,
            levelProgress: response.data.levelProgress,
            currentLevelExperience: response.data.currentLevelExperience,
            experienceToNextLevel: response.data.experienceToNextLevel
          });
          
          // Mark as solved for this session
          setHasAlreadySolved(true);
          
          // Show success modal
          setShowSuccessModal(true);
          setOutput('✅ Submission successful! Check the modal for details.');
        } else {
          // Show failed submission in console
          const status = '❌ WRONG ANSWER';
          let outputMsg = `🎯 Submission Results:\n${status}\n\n`;
          outputMsg += `📊 Passed: ${passedCount}/${totalCount} test cases\n`;
          outputMsg += `⏱️ Execution Time: ${submission.executionTime}ms\n`;
          outputMsg += `💾 Memory Used: ${submission.memoryUsed}MB\n\n`;
          outputMsg += `💡 ${totalCount - passedCount} test case(s) failed.\n`;
          outputMsg += `Review the failed cases and try again!`;
          setOutput(outputMsg);
        }
        
        // Show submissions tab
        setActiveTab('submissions');
      } else {
        setOutput(`❌ Submission failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Submission error:', err);
      setOutput(`❌ Error: ${err.response?.data?.error || err.message || 'Failed to submit code'}`);
    } finally {
      setIsRunning(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const submitSolution = async () => {
    // Check authentication
    if (!isAuthenticated()) {
      setShowAuthModal(true);
      return;
    }
    
    if (!code.trim()) return;
    
    setIsRunning(true);
    
    try {
      const response = await api.post('/api/submit-solution', {
        questionId: question.id,
        code,
        language
      });
      
      const submission = {
        id: Date.now(),
        code,
        language,
        status: response.data.status,
        runtime: response.data.runtime,
        memory: response.data.memory,
        timestamp: new Date().toISOString()
      };
      
      setSubmissions(prev => [submission, ...prev]);
    } catch (err) {
      console.error('Error submitting solution:', err);
    } finally {
      setIsRunning(false);
    }
  };

  const checkCodeQuality = async () => {
    if (!code.trim()) {
      setOutput('⚠️ Please write some code first');
      return;
    }

    setIsAnalyzingCode(true);
    setOutput('🔍 Analyzing code quality...');
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/api/code-quality/analyze',
        { code, language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCodeQualityResults(response.data);
      setShowCodeQuality(true);
      setOutput(`✅ Code analysis complete! Score: ${response.data.score}/100`);
    } catch (error) {
      console.error('Code quality check failed:', error);
      setOutput(`❌ Analysis failed: ${error.response?.data?.message || error.message}`);
      setCodeQualityResults(null);
    } finally {
      setIsAnalyzingCode(false);
    }
  };

  // Ghost Coding Functions
  const startRecording = async () => {
    if (!question) return;
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      setLoginPromptFeature('Ghost Coding');
      setShowLoginPrompt(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/api/ghost-coding/sessions/start',
        { 
          questionId: question.id, 
          language 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(response.data.id);
      setIsRecording(true);
      keyStrokesRef.current = [];
      setOutput('🔴 Recording started! Your keystrokes are being captured...');
      
      // Track code changes every 500ms
      recordingIntervalRef.current = setInterval(() => {
        // Use current code state instead of editor view
        keyStrokesRef.current.push({
          timestamp: Date.now(),
          code: code,
          action: 'edit'
        });
      }, 500);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setOutput(`❌ Failed to start recording: ${error.response?.data?.message || error.message}`);
    }
  };

  const stopRecording = async () => {
    if (!sessionId) return;
    
    try {
      clearInterval(recordingIntervalRef.current);
      
      // Record all keystrokes first
      const token = localStorage.getItem('token');
      for (const keystroke of keyStrokesRef.current) {
        await api.post(
          `/api/ghost-coding/sessions/${sessionId}/record`,
          keystroke,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Then complete the session
      await api.post(
        `/api/ghost-coding/sessions/${sessionId}/complete`,
        { 
          finalCode: code,
          isSuccessful: true,
          executionTimeMs: Date.now() - keyStrokesRef.current[0]?.timestamp,
          keystrokeCount: keyStrokesRef.current.length
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsRecording(false);
      setSessionId(null);
      keyStrokesRef.current = [];
      setOutput('✅ Recording saved! Others can now watch your coding session.');
    } catch (error) {
      console.error('Failed to stop recording:', error);
      setOutput(`❌ Failed to save recording: ${error.response?.data?.message || error.message}`);
    }
  };

  const loadRecordings = async () => {
    if (!question) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Load both featured recordings and user's own recordings
      const [featuredResponse, myResponse] = await Promise.all([
        api.get(
          `/api/ghost-coding/questions/${question.id}/featured`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: [] })),
        api.get(
          `/api/ghost-coding/sessions/my`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: [] }))
      ]);
      
      // Filter my recordings to only this question
      const myRecordings = myResponse.data.filter(r => r.questionId === question.id);
      
      // Combine and mark which is which
      const recordings = [
        ...featuredResponse.data.map(r => ({ ...r, isFeaturedSection: true })),
        ...myRecordings.map(r => ({ ...r, isMyRecording: true }))
      ];
      
      setAvailableRecordings(recordings);
      setShowRecordings(true);
    } catch (error) {
      console.error('Failed to load recordings:', error);
      setOutput(`❌ Failed to load recordings: ${error.response?.data?.message || error.message}`);
    }
  };

  const playRecording = async (recording) => {
    try {
      const token = localStorage.getItem('token');
      const sessionId = recording.sessionId || recording.id;
      const response = await api.get(
        `/api/ghost-coding/sessions/${sessionId}/playback`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const events = response.data.events;
      if (!events || events.length === 0) {
        setOutput('❌ No playback data available');
        return;
      }
      
      setIsPlayingBack(true);
      setShowRecordings(false);
      setCode(''); // Clear editor
      setOutput('▶️ Playing back session... Watch the code appear!');
      
      // Playback events
      for (let i = 0; i < events.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (events[i].code) {
          setCode(events[i].code);
        }
        setPlaybackProgress(((i + 1) / events.length) * 100);
      }
      
      setIsPlayingBack(false);
      setPlaybackProgress(0);
      setOutput('✅ Playback complete!');
    } catch (error) {
      console.error('Failed to play recording:', error);
      setOutput(`❌ Playback failed: ${error.response?.data?.message || error.message}`);
      setIsPlayingBack(false);
    }
  };

  // AI Functions
  const getAIHint = async () => {
    if (!question) return;
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      setLoginPromptFeature('AI Hint');
      setShowLoginPrompt(true);
      return;
    }
    
    try {
      setIsLoadingHint(true);
      setAiLoadingMessage('Generating AI Hint...');
      const token = localStorage.getItem('token');
      
      const response = await api.post(
        '/api/ai-assistant/hint',
        {
          questionTitle: question.title,
          questionDescription: question.description,
          currentCode: code,
          hintLevel: hintLevel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAIHint(response.data);
      setShowAIHint(true);
      setAiLoadingMessage('');
    } catch (error) {
      console.error('Failed to get AI hint:', error);
      setOutput('❌ Failed to get hint. Make sure OpenAI is configured.');
      setAiLoadingMessage('');
    } finally {
      setIsLoadingHint(false);
    }
  };

  const explainCode = async () => {
    if (!code) {
      setOutput('⚠️ Please write some code first!');
      return;
    }
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      setLoginPromptFeature('AI Code Explainer');
      setShowLoginPrompt(true);
      return;
    }
    
    // Get selected code from editor
    // Try to get CodeMirror view from DOM, fallback to browser selection
    let codeToExplain = code;
    
    // First try CodeMirror view
    if (editorViewRef.current) {
      const view = editorViewRef.current;
      const selection = view.state.selection.main;
      if (!selection.empty) {
        const selectedText = view.state.doc.sliceString(selection.from, selection.to);
        if (selectedText && selectedText.trim()) {
          codeToExplain = selectedText;
        }
      }
    } else if (editorContainerRef.current) {
      // Fallback to browser selection API
      const selection = window.getSelection();
      if (selection && selection.toString().trim()) {
        const selectedText = selection.toString();
        if (selectedText && selectedText.trim()) {
          codeToExplain = selectedText;
        }
      }
    }
    
    try {
      setIsLoadingExplanation(true);
      setAiLoadingMessage('Explaining your code...');
      const token = localStorage.getItem('token');
      
      const response = await api.post(
        '/api/ai-assistant/explain-code',
        {
          code: codeToExplain,
          language: language,
          context: question ? `Problem: ${question.title}` : ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAIExplanation(response.data.explanation);
      setSelectedCode(codeToExplain);
      setShowAIExplainer(true);
      setAiLoadingMessage('');
    } catch (error) {
      console.error('Failed to explain code:', error);
      setOutput('❌ Failed to explain code. Make sure OpenAI is configured.');
      setAiLoadingMessage('');
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _debugWithAI = async (errorMsg) => { // Reserved for future use
    if (!code) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await api.post(
        '/api/ai-assistant/debug-error',
        {
          code: code,
          errorMessage: errorMsg,
          language: language
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Display AI debugging help in output
      setOutput(`🤖 AI Debugger:\n\n${response.data.explanation}`);
    } catch (error) {
      console.error('Failed to debug with AI:', error);
    }
  };

  const analyzeComplexity = async () => {
    if (!code) {
      setOutput('⚠️ Please write some code first!');
      return;
    }
    
    try {
      setIsAnalyzingComplexity(true);
      setAiLoadingMessage('Analyzing complexity...');
      const token = localStorage.getItem('token');
      
      const response = await api.post(
        '/api/ai-assistant/analyze-complexity',
        {
          code: code,
          language: language,
          problemContext: question ? `Problem: ${question.title}` : ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setComplexityAnalysis(response.data.analysis);
      setShowComplexityAnalysis(true);
      setAiLoadingMessage('');
    } catch (error) {
      console.error('Failed to analyze complexity:', error);
      setOutput('❌ Failed to analyze complexity. Make sure OpenAI is configured.');
      setAiLoadingMessage('');
    } finally {
      setIsAnalyzingComplexity(false);
    }
  };

  const resetCode = () => {
    const questionId = searchParams.get('questionId') || searchParams.get('question');
    if (!questionId) {
      return;
    }
    
    // Load initial state
    const initialState = loadInitialState(questionId);
    
    if (initialState) {
      // Restore initial state
      setCode(initialState.code || '');
      setLanguage(initialState.language || 'javascript');
      setCustomInput(initialState.customInput || '');
      setUseCustomInput(initialState.useCustomInput || false);
    } else {
      // Fallback to question's initial code or default
      if (question && question.initialCode && question.initialCode[language]) {
        setCode(question.initialCode[language]);
      } else {
        setCode(getDefaultCode(language));
      }
      setCustomInput('');
      setUseCustomInput(false);
    }
    
    // Clear saved state from localStorage when resetting
    clearEditorState(questionId);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Tour steps configuration
  const tourSteps = [
    {
      target: '[data-tour="problem-panel"]',
      content: 'This is the problem statement panel. Read the problem description, examples, and constraints here. You can toggle this panel on/off.',
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="editor"]',
      content: 'This is the code editor. Write your solution here. The editor supports syntax highlighting and auto-completion for multiple languages.',
      placement: 'left',
    },
    {
      target: '[data-tour="language-selector"]',
      content: 'Select your preferred programming language. The editor will adapt to the selected language.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="run-code"]',
      content: 'Click "Run Code" to test your solution against visible test cases. This helps you debug before submitting.',
      placement: 'top',
    },
    {
      target: '[data-tour="submit"]',
      content: 'Click "Submit" to submit your solution. It will be tested against all test cases (including hidden ones).',
      placement: 'top',
    },
    {
      target: '[data-tour="reset"]',
      content: 'Click "Reset" to restore the editor to its initial state, clearing all your changes.',
      placement: 'top',
    },
    {
      target: '[data-tour="test-cases-tab"]',
      content: 'View test cases here. You can see the input and expected output for each test case.',
      placement: 'right',
    },
    {
      target: '[data-tour="ai-tools"]',
      content: 'Access AI-powered features like hints, explanations, code analysis, and complexity analysis to help you learn.',
      placement: 'left',
    },
    {
      target: '[data-tour="code-quality"]',
      content: 'Check your code quality to get suggestions for improvements and best practices.',
      placement: 'top',
    },
    {
      target: '[data-tour="record"]',
      content: 'Record your coding session to create a replayable solution. Great for learning and sharing your approach!',
      placement: 'top',
    },
    {
      target: '[data-tour="solution-recordings"]',
      content: 'View and replay solution recordings from other users to learn different approaches to solving the problem.',
      placement: 'top',
    },
    {
      target: '[data-tour="submissions-tab"]',
      content: 'View your submission history here. See all your previous attempts and their results.',
      placement: 'right',
    },
  ];
  
  // Check if user has seen the tour before
  useEffect(() => {
    if (!question || loading) return;
    
    // For non-authenticated users: always show tour automatically
    if (!isAuthenticated()) {
      setTimeout(() => {
        setRunTour(true);
      }, 1000);
      return;
    }
    
    // For authenticated users: only show tour if they haven't seen it before
    if (user?.id) {
      const tourKey = `questionIDE_tour_completed_${user.id}`;
      const hasSeenTour = localStorage.getItem(tourKey);
      if (!hasSeenTour) {
        setTimeout(() => {
          setRunTour(true);
        }, 1000);
      }
    }
  }, [question, loading, isAuthenticated, user?.id]);
  
  // Handle tour completion
  const handleTourCallback = (data) => {
    const { status, action, type } = data;
    
    // Stop tour if finished, skipped, or closed
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === 'close' || type === 'tour:end') {
      // Only save completion status for authenticated users
      if (isAuthenticated() && user?.id) {
        const tourKey = `questionIDE_tour_completed_${user.id}`;
        localStorage.setItem(tourKey, 'true');
      }
      // For non-authenticated users, don't save - they'll see it every time
      setRunTour(false);
    }
  };

  // Navigate to next/previous question (filtered by difficulty level)
  const navigateToQuestion = (direction) => {
    if (sectionQuestions.length === 0 || currentQuestionIndex === -1 || !question) return;
    
    // Get current question's difficulty
    const currentDifficulty = question.difficultyLevel || question.difficulty || 'MEDIUM';
    
    // Filter questions by current difficulty level
    const sameDifficultyQuestions = sectionQuestions
      .map((q, idx) => ({ ...q, originalIndex: idx }))
      .filter(q => {
        const qDifficulty = q.difficultyLevel || q.difficulty || 'MEDIUM';
        return qDifficulty === currentDifficulty;
      });
    
    // Find current question's index within same difficulty questions
    const currentDifficultyIndex = sameDifficultyQuestions.findIndex(
      q => (q.id === parseInt(question.id)) || (q.id === question.id)
    );
    
    if (currentDifficultyIndex === -1) return;
    
    let newDifficultyIndex;
    if (direction === 'next') {
      newDifficultyIndex = currentDifficultyIndex + 1;
      // If at last question of current difficulty, move to next difficulty level
      if (newDifficultyIndex >= sameDifficultyQuestions.length) {
        // Find next difficulty level
        const difficultyOrder = ['EASY', 'MEDIUM', 'HARD'];
        const currentDiffIndex = difficultyOrder.indexOf(currentDifficulty);
        
        if (currentDiffIndex < difficultyOrder.length - 1) {
          // Move to next difficulty
          const nextDifficulty = difficultyOrder[currentDiffIndex + 1];
          const nextDifficultyQuestions = sectionQuestions
            .map((q, idx) => ({ ...q, originalIndex: idx }))
            .filter(q => {
              const qDifficulty = q.difficultyLevel || q.difficulty || 'MEDIUM';
              return qDifficulty === nextDifficulty;
            });
          
          if (nextDifficultyQuestions.length > 0) {
            const nextQuestion = nextDifficultyQuestions[0];
            navigateToQuestionById(nextQuestion.id);
            return;
          }
        }
        return; // No more questions
      }
    } else {
      newDifficultyIndex = currentDifficultyIndex - 1;
      // If at first question of current difficulty, move to previous difficulty level
      if (newDifficultyIndex < 0) {
        // Find previous difficulty level
        const difficultyOrder = ['EASY', 'MEDIUM', 'HARD'];
        const currentDiffIndex = difficultyOrder.indexOf(currentDifficulty);
        
        if (currentDiffIndex > 0) {
          // Move to previous difficulty
          const prevDifficulty = difficultyOrder[currentDiffIndex - 1];
          const prevDifficultyQuestions = sectionQuestions
            .map((q, idx) => ({ ...q, originalIndex: idx }))
            .filter(q => {
              const qDifficulty = q.difficultyLevel || q.difficulty || 'MEDIUM';
              return qDifficulty === prevDifficulty;
            });
          
          if (prevDifficultyQuestions.length > 0) {
            const nextQuestion = prevDifficultyQuestions[prevDifficultyQuestions.length - 1];
            navigateToQuestionById(nextQuestion.id);
            return;
          }
        }
        return; // No more questions
      }
    }
    
    const nextQuestion = sameDifficultyQuestions[newDifficultyIndex];
    if (!nextQuestion || !nextQuestion.id) return;
    
    navigateToQuestionById(nextQuestion.id);
  };
  
  // Helper function to navigate to a question by ID
  const navigateToQuestionById = (questionId) => {
    // Get current URL params
    const courseId = searchParams.get('courseId');
    const packageId = searchParams.get('packageId');
    const sectionId = question?.sectionId || searchParams.get('sectionId');
    
    // Build new URL
    let newUrl = `/question-ide?questionId=${questionId}`;
    if (courseId) {
      newUrl += `&courseId=${courseId}`;
    }
    if (packageId) {
      newUrl += `&packageId=${packageId}`;
    }
    if (sectionId) {
      newUrl += `&sectionId=${sectionId}`;
    }
    
    // Navigate to next question - this will trigger a reload
    window.location.href = newUrl;
  };
  
  // Get navigation info (current position within difficulty, total in difficulty)
  const getNavigationInfo = () => {
    if (sectionQuestions.length === 0 || currentQuestionIndex === -1 || !question) {
      return { current: 0, total: 0, hasNext: false, hasPrev: false };
    }
    
    const currentDifficulty = question.difficultyLevel || question.difficulty || 'MEDIUM';
    const sameDifficultyQuestions = sectionQuestions.filter(q => {
      const qDifficulty = q.difficultyLevel || q.difficulty || 'MEDIUM';
      return qDifficulty === currentDifficulty;
    });
    
    const currentDifficultyIndex = sameDifficultyQuestions.findIndex(
      q => (q.id === parseInt(question.id)) || (q.id === question.id)
    );
    
    if (currentDifficultyIndex === -1) {
      return { current: 0, total: 0, hasNext: false, hasPrev: false };
    }
    
    const hasNext = currentDifficultyIndex < sameDifficultyQuestions.length - 1 || 
      (() => {
        const difficultyOrder = ['EASY', 'MEDIUM', 'HARD'];
        const currentDiffIndex = difficultyOrder.indexOf(currentDifficulty);
        if (currentDiffIndex < difficultyOrder.length - 1) {
          const nextDifficulty = difficultyOrder[currentDiffIndex + 1];
          return sectionQuestions.some(q => {
            const qDifficulty = q.difficultyLevel || q.difficulty || 'MEDIUM';
            return qDifficulty === nextDifficulty;
          });
        }
        return false;
      })();
    
    const hasPrev = currentDifficultyIndex > 0 || 
      (() => {
        const difficultyOrder = ['EASY', 'MEDIUM', 'HARD'];
        const currentDiffIndex = difficultyOrder.indexOf(currentDifficulty);
        if (currentDiffIndex > 0) {
          const prevDifficulty = difficultyOrder[currentDiffIndex - 1];
          return sectionQuestions.some(q => {
            const qDifficulty = q.difficultyLevel || q.difficulty || 'MEDIUM';
            return qDifficulty === prevDifficulty;
          });
        }
        return false;
      })();
    
    return {
      current: currentDifficultyIndex + 1,
      total: sameDifficultyQuestions.length,
      hasNext,
      hasPrev,
      difficulty: currentDifficulty
    };
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4 ${
            theme === 'dark' ? 'border-blue-400' : 'border-blue-600'
          }`}></div>
          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>Loading question...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className={`text-2xl font-bold mb-2 ${
            theme === 'dark' ? 'text-white' : 'text-gray-800'
          }`}>Error</h2>
          <p className={`mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>{error}</p>
          <button
            onClick={() => navigate('/courses')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className={`text-6xl mb-4 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>❓</div>
          <h2 className={`text-2xl font-bold mb-2 ${
            theme === 'dark' ? 'text-white' : 'text-gray-800'
          }`}>No Question Found</h2>
          <p className={`mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>The requested question could not be found.</p>
          <button
            onClick={() => navigate('/courses')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`} style={{ height: '100vh', paddingTop: isMobile ? '70px' : '90px', overflow: isMobile ? 'auto' : 'hidden', WebkitOverflowScrolling: 'touch' }}>
      {/* Tour - Only show on desktop */}
      {!isMobile && (
        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          callback={handleTourCallback}
          styles={{
            options: {
              primaryColor: '#667eea',
              zIndex: 10000,
            },
          }}
        />
      )}
      {/* Header - Modern Single Row Design */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} px-3 sm:px-6 py-2 sm:py-3 flex-shrink-0`} style={{ marginTop: isMobile ? '10px' : '10px' }}>
        {isMobile ? (
          /* Mobile: Stacked Layout */
          <div className="flex flex-col gap-2">
            {/* Top Row: Back + Title + Badge */}
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => {
                  const packageId = searchParams.get('packageId');
                  if (packageId) {
                    navigate(`/question-packages/${packageId}`);
                  } else {
                    navigate(-1);
                  }
                }}
                className={`flex-shrink-0 font-medium text-base ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-300' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                ←
              </button>
              
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <h1 className={`text-sm font-semibold truncate flex-1 min-w-0 text-left m-0 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {question.title}
                </h1>
                
                {/* Difficulty Badge */}
                <span className={`px-2 py-0.5 rounded-md text-xs font-semibold flex-shrink-0 ${
                (question.difficultyLevel || question.difficulty || 'Medium').toUpperCase() === 'EASY'
                  ? theme === 'dark'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-green-100 text-green-700 border border-green-300'
                  : (question.difficultyLevel || question.difficulty || 'Medium').toUpperCase() === 'MEDIUM'
                  ? theme === 'dark'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  : theme === 'dark'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-red-100 text-red-700 border border-red-300'
              }`}>
                {question.difficultyLevel || question.difficulty || 'Medium'}
              </span>
              </div>
            </div>
            
            {/* Bottom Row: Navigation + Controls */}
            <div className="flex items-center justify-between gap-2">
              {/* Navigation */}
              {(() => {
                const navInfo = getNavigationInfo();
                if (sectionQuestions.length > 1 && navInfo.total > 0) {
                  return (
                    <div className={`flex items-center gap-1 flex-shrink-0 px-2 py-1 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-white border border-gray-200'
                    }`}>
                      <button
                        onClick={() => navigateToQuestion('prev')}
                        disabled={!navInfo.hasPrev}
                        className={`px-1.5 py-0.5 rounded text-xs font-medium transition-all ${
                          !navInfo.hasPrev
                            ? 'opacity-30 cursor-not-allowed text-gray-400'
                            : theme === 'dark'
                              ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        title="Previous"
                      >
                        ←
                      </button>
                      <span className={`text-xs px-1.5 font-medium ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        {navInfo.current}/{navInfo.total}
                      </span>
                      <button
                        onClick={() => navigateToQuestion('next')}
                        disabled={!navInfo.hasNext}
                        className={`px-1.5 py-0.5 rounded text-xs font-medium transition-all ${
                          !navInfo.hasNext
                            ? 'opacity-30 cursor-not-allowed text-gray-400'
                            : theme === 'dark'
                              ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        title="Next"
                      >
                        →
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Controls */}
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                <select
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className={`px-1.5 py-1 rounded text-xs border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-800'
                  }`}
                >
                  <option value="javascript">JS</option>
                  <option value="python">Py</option>
                  <option value="java">Java</option>
                  <option value="cpp">C++</option>
                  <option value="c">C</option>
                </select>
                
                <button
                  onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                  className={`px-2 py-1 rounded text-sm transition-all ${
                    theme === 'dark' 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
                >
                  {theme === 'dark' ? '☀️' : '🌙'}
                </button>
                
                <button
                  onClick={() => setShowQuestionPanel(!showQuestionPanel)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                    theme === 'dark'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                >
                  {showQuestionPanel ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Desktop: Single Row Layout */
          <div className="flex items-center justify-between gap-4">
          {/* Left: Back + Title + Navigation */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <button
              onClick={() => {
                const packageId = searchParams.get('packageId');
                if (packageId) {
                  navigate(`/question-packages/${packageId}`);
                } else {
                  navigate(-1);
                }
              }}
              className={`flex-shrink-0 font-medium text-sm ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-300' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ←
            </button>
            
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex items-center gap-0 flex-1 min-w-0">
                <h1 className={`text-base sm:text-lg font-semibold break-words flex-1 min-w-0 text-left m-0 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {question.title}
                </h1>
                
                {/* Difficulty Badge - Immediately after Title */}
                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold flex-shrink-0 ${
                (question.difficultyLevel || question.difficulty || 'Medium').toUpperCase() === 'EASY'
                  ? theme === 'dark'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-green-100 text-green-700 border border-green-300'
                  : (question.difficultyLevel || question.difficulty || 'Medium').toUpperCase() === 'MEDIUM'
                  ? theme === 'dark'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  : theme === 'dark'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-red-100 text-red-700 border border-red-300'
              }`}>
                {question.difficultyLevel || question.difficulty || 'Medium'}
              </span>
              </div>
              
              {/* Inline Navigation */}
              {(() => {
                const navInfo = getNavigationInfo();
                if (sectionQuestions.length > 1 && navInfo.total > 0) {
                  return (
                    <div className={`flex items-center gap-1 flex-shrink-0 px-2 py-1 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-white border border-gray-200'
                    }`}>
                      <button
                        onClick={() => navigateToQuestion('prev')}
                        disabled={!navInfo.hasPrev}
                        className={`px-1.5 py-0.5 rounded text-xs font-medium transition-all ${
                          !navInfo.hasPrev
                            ? 'opacity-30 cursor-not-allowed text-gray-400'
                            : theme === 'dark'
                              ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        title="Previous"
                      >
                        ←
                      </button>
                      <span className={`text-xs px-2 font-medium ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        {navInfo.current}/{navInfo.total}
                      </span>
                      <button
                        onClick={() => navigateToQuestion('next')}
                        disabled={!navInfo.hasNext}
                        className={`px-1.5 py-0.5 rounded text-xs font-medium transition-all ${
                          !navInfo.hasNext
                            ? 'opacity-30 cursor-not-allowed text-gray-400'
                            : theme === 'dark'
                              ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        title="Next"
                      >
                        →
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
          
          {/* Right: Controls */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Language Selector - Compact */}
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className={`px-2 py-1 rounded text-xs border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-800'
              }`}
            >
              <option value="javascript">JS</option>
              <option value="python">Py</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
              <option value="c">C</option>
            </select>
            
            {/* Theme Toggle - Icon Only */}
            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className={`px-2.5 py-1 rounded text-sm transition-all ${
                theme === 'dark' 
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              title={theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            
            {/* Problem Panel Toggle */}
            <button
              onClick={() => setShowQuestionPanel(!showQuestionPanel)}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                theme === 'dark'
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {showQuestionPanel ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        )}
      </div>
      <div className={`flex ${isMobile ? 'flex-col' : 'sm:flex-row'} flex-1 ${isMobile ? 'overflow-y-auto' : 'overflow-hidden'}`} style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
        {/* Left Panel - Question */}
        {showQuestionPanel && (
          <div 
            ref={leftPanelRef}
            data-tour="problem-panel"
            className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-200'} backdrop-blur-sm border-r flex flex-col shadow-xl w-full sm:w-auto ${isMobile ? 'overflow-visible' : 'overflow-hidden'}`}
            style={{ 
              width: isMobile ? '100%' : `${leftPanelWidth}px`, 
              minHeight: isMobile ? 'auto' : 0, 
              flexShrink: isMobile ? 0 : 1,
              willChange: isResizing ? 'width' : 'auto',
              transform: 'translateZ(0)'
            }}
          >
            {/* Resize Handle - Hidden on mobile */}
            {!isMobile && (
              <div
                ref={resizeRef}
                className={`absolute right-0 top-0 w-1 h-full cursor-col-resize z-10 transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500' : 'bg-purple-300 hover:bg-purple-500'
                }`}
                style={{
                  willChange: 'background-color',
                  transform: 'translateZ(0)'
                }}
                onMouseDown={handleMouseDown}
                title="Drag to resize panel"
              />
            )}
            
            {/* Tabs with Close Button */}
            <div className={`border-b overflow-x-auto relative ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex min-w-max sm:min-w-0">
                {['problem', 'testcases', 'submissions', 'solution'].map((tab) => (
                  <button
                    key={tab}
                    data-tour={tab === 'testcases' ? 'test-cases-tab' : tab === 'submissions' ? 'submissions-tab' : undefined}
                    onClick={() => setActiveTab(tab)}
                    className={`${isMobile ? 'px-3 py-2 text-sm' : 'px-5 py-3 text-base'} font-medium capitalize transition-colors whitespace-nowrap ${
                      activeTab === tab
                        ? theme === 'dark'
                          ? 'text-white border-b-2 border-white'
                          : 'text-gray-900 border-b-2 border-gray-900'
                        : theme === 'dark'
                          ? 'text-gray-400 hover:text-gray-300'
                          : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              {/* Close Button - Positioned absolutely in top right */}
              <button
                onClick={() => setShowQuestionPanel(false)}
                className={`absolute top-0 right-0 ${isMobile ? 'px-2 py-1.5 text-base' : 'px-3 py-2 text-lg'} font-bold transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="Hide Problem Panel"
              >
                ×
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4" style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
              {activeTab === 'problem' && (
                <div className="space-y-4 sm:space-y-6">
                  {/* Problem Statement */}
                  <div>
                    {/* Problem Statement Card */}
                    <div className={`relative overflow-hidden rounded-xl border shadow-sm ${
                      theme === 'dark' 
                        ? 'border-violet-700 bg-gray-800' 
                        : 'border-violet-200 bg-white'
                    }`}>
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-violet-500 to-purple-600"></div>
                      <div className="p-3 sm:p-5 sm:pl-6 pl-4">
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
                            <span className="text-white text-base sm:text-lg">📋</span>
                          </div>
                          <h3 className={`text-base sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>Problem Statement</h3>
                        </div>
                        <div className={`prose max-w-none leading-relaxed text-left text-sm sm:text-base ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                        <ReactMarkdown
                          components={{
                              h1: ({children, ...props}) => <h1 className={`text-lg sm:text-xl font-bold mb-2 sm:mb-3 mt-3 sm:mt-4 text-left ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`} {...props}>{children}</h1>,
                              h2: ({children, ...props}) => <h2 className={`text-base sm:text-lg font-bold mb-2 mt-2 sm:mt-3 text-left ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                              }`} {...props}>{children}</h2>,
                              h3: ({children, ...props}) => <h3 className={`text-sm sm:text-base font-bold mb-1 sm:mb-2 mt-2 text-left ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                              }`} {...props}>{children}</h3>,
                              strong: (props) => <strong className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`} {...props} />,
                              em: (props) => <em className={`italic ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`} {...props} />,
                              ul: (props) => <ul className="list-disc list-inside my-2 space-y-1 text-left" {...props} />,
                              ol: (props) => <ol className="list-decimal list-inside my-2 space-y-1 text-left" {...props} />,
                              li: (props) => <li className="ml-2 text-left" {...props} />,
                              code: ({className, children, ...props}) => {
                                const isBlock = className?.includes('language-');
                                return isBlock 
                                  ? <code className="block bg-gray-900 text-emerald-400 p-2 sm:p-3 rounded-lg my-2 text-xs sm:text-sm font-mono overflow-x-auto text-left" {...props}>{children}</code>
                                  : <code className={`px-1 sm:px-1.5 py-0.5 rounded text-xs sm:text-sm font-mono ${
                                    theme === 'dark' 
                                      ? 'bg-violet-900 text-violet-300' 
                                      : 'bg-violet-100 text-violet-700'
                                  }`} {...props}>{children}</code>;
                              },
                              pre: ({children, ...props}) => <pre className="bg-gray-900 text-emerald-400 p-3 sm:p-4 rounded-lg my-2 sm:my-3 text-xs sm:text-sm font-mono overflow-x-auto" {...props}>{children}</pre>,
                              p: (props) => <p className={`mb-2 sm:mb-3 leading-relaxed text-left text-sm sm:text-base ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }`} {...props} />,
                          }}
                        >
                          {question.problemStatement || question.description}
                        </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Examples Card */}
                  {question.examples && (
                    <div className={`relative overflow-hidden rounded-xl border shadow-sm ${
                      theme === 'dark' 
                        ? 'border-blue-700 bg-gray-800' 
                        : 'border-blue-200 bg-white'
                    }`}>
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-cyan-500"></div>
                      <div className="p-3 sm:p-5 sm:pl-6 pl-4">
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                            <span className="text-white text-base sm:text-lg">💡</span>
                        </div>
                          <h3 className={`text-base sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>Examples</h3>
                        </div>
                        {renderExamples(question.examples)}
                      </div>
                    </div>
                  )}
                  
                  {/* Constraints Card */}
                  {question.constraints && (
                    <div className={`relative overflow-hidden rounded-xl border shadow-sm ${
                      theme === 'dark' 
                        ? 'border-amber-700 bg-gray-800' 
                        : 'border-amber-200 bg-white'
                    }`}>
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-orange-500"></div>
                      <div className="p-3 sm:p-5 sm:pl-6 pl-4">
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg flex-shrink-0">
                            <span className="text-white text-base sm:text-lg">⚡</span>
                          </div>
                          <h3 className={`text-base sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>Constraints</h3>
                        </div>
                        <div className={`prose max-w-none text-xs sm:text-sm text-left ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          <ReactMarkdown
                            components={{
                              h1: ({children, ...props}) => <h1 className={`text-base sm:text-lg font-bold mb-2 text-left ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`} {...props}>{children}</h1>,
                              h2: ({children, ...props}) => <h2 className={`text-sm sm:text-base font-bold mb-2 text-left ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                              }`} {...props}>{children}</h2>,
                              h3: ({children, ...props}) => <h3 className={`text-xs sm:text-sm font-bold mb-1 text-left ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                              }`} {...props}>{children}</h3>,
                              strong: (props) => <strong className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`} {...props} />,
                              em: (props) => <em className={`italic ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`} {...props} />,
                              ul: (props) => <ul className="list-disc list-inside my-2 space-y-1 text-left" {...props} />,
                              ol: (props) => <ol className="list-decimal list-inside my-2 space-y-1 text-left" {...props} />,
                              li: (props) => <li className={`ml-2 text-left ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }`} {...props} />,
                              code: ({className, children, ...props}) => {
                                const isBlock = className?.includes('language-');
                                return isBlock 
                                  ? <code className="block bg-gray-900 text-emerald-400 p-2 sm:p-3 rounded-lg my-2 text-xs sm:text-sm font-mono overflow-x-auto text-left" {...props}>{children}</code>
                                  : <code className={`px-1 sm:px-1.5 py-0.5 rounded text-xs sm:text-sm font-mono ${
                                    theme === 'dark' 
                                      ? 'bg-amber-900 text-amber-300' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`} {...props}>{children}</code>;
                              },
                              pre: ({children, ...props}) => <pre className="bg-gray-900 text-emerald-400 p-2 sm:p-3 rounded-lg my-2 text-xs sm:text-sm font-mono overflow-x-auto" {...props}>{children}</pre>,
                              p: (props) => <p className={`mb-2 text-left text-xs sm:text-sm ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }`} {...props} />,
                            }}
                          >
                          {question.constraints}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Time and Memory Limits */}
                  {(question.timeLimitSeconds || question.memoryLimitMB) && (
                    <div>
                      <h3 className={`text-lg font-semibold mb-3 flex items-center ${
                        theme === 'dark' ? 'text-white' : 'text-gray-800'
                      }`}>
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Limits
                      </h3>
                      <div className={`p-4 rounded-lg border ${
                        theme === 'dark' 
                          ? 'bg-gray-800 border-green-700' 
                          : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                      }`}>
                        <div className={`flex space-x-4 text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {question.timeLimitSeconds && (
                            <div className="flex items-center">
                              <span className="font-medium mr-1">⏱️ Time Limit:</span>
                              <span>{question.timeLimitSeconds}s</span>
                            </div>
                          )}
                          {question.memoryLimitMB && (
                            <div className="flex items-center">
                              <span className="font-medium mr-1">💾 Memory Limit:</span>
                              <span>{question.memoryLimitMB}MB</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'testcases' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-lg font-semibold flex items-center ${
                      theme === 'dark' ? 'text-white' : 'text-gray-800'
                    }`}>
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      Test Cases
                    </h3>
                  </div>
                  
                  {testCases.length === 0 ? (
                    <div className={`text-center py-8 rounded-lg ${
                      theme === 'dark' 
                        ? 'text-gray-400 bg-gray-800' 
                        : 'text-gray-500 bg-gradient-to-r from-gray-50 to-gray-100'
                    }`}>
                      <div className="text-4xl mb-2">🧪</div>
                      <p>No test cases available</p>
                    </div>
                  ) : (
                    testCases.map((testCase, index) => (
                      <div key={index} className={`p-4 rounded-lg border ${
                        theme === 'dark' 
                          ? 'bg-gray-800 border-blue-700' 
                          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                      }`}>
                        <div className={`font-medium mb-3 flex items-center ${
                          theme === 'dark' ? 'text-white' : 'text-gray-800'
                        }`}>
                          <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2">
                            {index + 1}
                          </span>
                          Test Case {index + 1}
                        </div>
                        <div className={`text-sm space-y-3 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          <div className={`p-3 rounded-lg border shadow-sm ${
                            theme === 'dark' 
                              ? 'bg-gray-900 border-gray-700' 
                              : 'bg-white border-gray-200'
                          }`}>
                            <strong className={`block mb-2 font-semibold ${
                              theme === 'dark' ? 'text-white' : 'text-gray-800'
                            }`}>Input:</strong>
                            <pre className={`p-3 rounded text-sm font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre break-words border leading-relaxed ${
                              theme === 'dark' 
                                ? 'bg-gray-950 text-gray-300 border-gray-700' 
                                : 'bg-gray-50 text-gray-900 border-gray-300'
                            }`} style={{ whiteSpace: 'pre-wrap' }}>
                              {normalizeNewlines(testCase.input || '')}
                            </pre>
                          </div>
                          <div className={`p-3 rounded-lg border shadow-sm ${
                            theme === 'dark' 
                              ? 'bg-gray-900 border-gray-700' 
                              : 'bg-white border-gray-200'
                          }`}>
                            <strong className={`block mb-2 font-semibold ${
                              theme === 'dark' ? 'text-white' : 'text-gray-800'
                            }`}>Expected:</strong>
                            <pre className={`p-3 rounded text-sm font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre break-words border leading-relaxed ${
                              theme === 'dark' 
                                ? 'bg-gray-950 text-emerald-300 border-gray-700' 
                                : 'bg-emerald-50 text-gray-900 border-emerald-300'
                            }`} style={{ whiteSpace: 'pre-wrap' }}>
                              {normalizeNewlines(testCase.expected || '')}
                            </pre>
                          </div>
                          {testResults[index] && (
                            <div className={`font-medium p-2 rounded ${
                              testResults[index].passed 
                                ? theme === 'dark'
                                  ? 'text-green-300 bg-green-900/30 border border-green-700'
                                  : 'text-green-700 bg-green-100 border border-green-300'
                                : theme === 'dark'
                                  ? 'text-red-300 bg-red-900/30 border border-red-700'
                                  : 'text-red-700 bg-red-100 border border-red-300'
                            }`}>
                              <strong>Result:</strong> {testResults[index].passed ? '✅ PASSED' : '❌ FAILED'}
                              {!testResults[index].passed && (
                                <div className={`mt-3 p-3 rounded-lg border shadow-sm ${
                                  theme === 'dark' 
                                    ? 'bg-gray-900 border-gray-700' 
                                    : 'bg-white border-red-200'
                                }`}>
                                  <strong className={`block mb-2 font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                                  }`}>Actual:</strong>
                                  <pre className={`p-3 rounded text-sm font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words border leading-relaxed ${
                                    theme === 'dark' 
                                      ? 'bg-gray-950 text-red-300 border-gray-700' 
                                      : 'bg-red-50 text-gray-900 border-red-300'
                                  }`}>
                                    {testResults[index].actual || ''}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'submissions' && (
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold flex items-center ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Submission History
                  </h3>
                  
                  {submissions.length === 0 ? (
                    <div className={`text-center py-8 rounded-lg ${
                      theme === 'dark' 
                        ? 'text-gray-400 bg-gray-800' 
                        : 'text-gray-500 bg-gradient-to-r from-gray-50 to-gray-100'
                    }`}>
                      <div className="text-4xl mb-2">📊</div>
                      <p>No submissions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {submissions.map((submission) => (
                        <div key={submission.id} className={`p-4 rounded-lg border ${
                          theme === 'dark' 
                            ? 'bg-gray-800 border-purple-700' 
                            : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-800'
                            }`}>
                              {new Date(submission.timestamp).toLocaleString()}
                            </span>
                            <div className="flex items-center space-x-2">
                              <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                                submission.status === 'ACCEPTED' 
                                  ? theme === 'dark'
                                    ? 'bg-green-900/30 text-green-300 border border-green-700'
                                    : 'bg-green-100 text-green-800 border border-green-300'
                                  : theme === 'dark'
                                    ? 'bg-red-900/30 text-red-300 border border-red-700'
                                    : 'bg-red-100 text-red-800 border border-red-300'
                              }`}>
                                {submission.status === 'ACCEPTED' ? '✅' : '❌'} {submission.status === 'ACCEPTED' ? 'Accepted' : 'Wrong Answer'}
                              </span>
                              <button
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setShowSubmissionModal(true);
                                }}
                                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg font-medium transition-colors"
                              >
                                👁️ View Code
                              </button>
                            </div>
                          </div>
                          <div className={`text-xs space-y-1 p-2 rounded border ${
                            theme === 'dark' 
                              ? 'text-gray-300 bg-gray-900 border-gray-700' 
                              : 'text-gray-600 bg-white border-gray-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <span><strong>Language:</strong> {submission.language}</span>
                              <span><strong>Tests:</strong> {submission.passedTests}/{submission.totalTests}</span>
                            </div>
                            {submission.executionTime && <div><strong>Runtime:</strong> {submission.executionTime}ms</div>}
                            {submission.memoryUsed && <div><strong>Memory:</strong> {submission.memoryUsed}MB</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'solution' && (
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold flex items-center ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Solution
                  </h3>
                  
                  {!isAuthenticated() ? (
                    <div className={`p-6 rounded-lg border ${
                      theme === 'dark' 
                        ? 'bg-gray-800 border-amber-700' 
                        : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'
                    }`}>
                      <div className="text-center space-y-4">
                        <div className="text-4xl mb-2">🔒</div>
                        <h4 className={`text-xl font-semibold ${
                          theme === 'dark' ? 'text-white' : 'text-gray-800'
                        }`}>
                          Login Required
                        </h4>
                        <p className={`text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          Please log in to view the solution for this question.
                        </p>
                        <button
                          onClick={() => setShowAuthModal(true)}
                          className="px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-lg text-base font-medium shadow-md transition-all"
                        >
                          Log In
                        </button>
                      </div>
                    </div>
                  ) : !showSolution ? (
                    <div className={`p-6 rounded-lg border ${
                      theme === 'dark' 
                        ? 'bg-gray-800 border-green-700' 
                        : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                    }`}>
                      <div className="text-center space-y-4">
                        <div className="text-4xl mb-2">💡</div>
                        <h4 className={`text-xl font-semibold ${
                          theme === 'dark' ? 'text-white' : 'text-gray-800'
                        }`}>
                          {hasAlreadySolved ? 'View Solution' : hasViewedSolution ? 'View Solution (XP Forfeited)' : 'Show Solution'}
                        </h4>
                        {!hasAlreadySolved && !hasViewedSolution && (
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            ⚠️ Viewing the solution before solving will forfeit XP rewards, but you can still learn from it!
                          </p>
                        )}
                        <button
                          data-tour="show-solution"
                          onClick={async () => {
                            // If user has already solved the question OR already viewed solution, show it directly
                            if (hasAlreadySolved || hasViewedSolution) {
                              setShowSolution(true);
                            } else {
                              // Show warning modal first
                              setShowSolutionWarning(true);
                            }
                          }}
                          className={`px-8 py-3 text-white rounded-lg text-base font-medium shadow-md transition-all ${
                            hasAlreadySolved
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                              : hasViewedSolution 
                                ? 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                                : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                          }`}
                        >
                          💡 {hasAlreadySolved ? 'View Solution ✓' : hasViewedSolution ? 'View Solution (XP Forfeited)' : 'Show Solution'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {question?.solution || question?.solutionCode ? (
                        <>
                          {question.solution && (
                            <div className={`p-5 rounded-xl border ${
                              theme === 'dark' 
                                ? 'bg-gray-800 border-blue-700' 
                                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                            }`}>
                              <h4 className={`text-lg font-bold mb-3 flex items-center ${
                                theme === 'dark' ? 'text-white' : 'text-gray-800'
                              }`}>
                                <span className="text-2xl mr-2">📖</span>
                                Explanation
                              </h4>
                              <div className={`leading-relaxed text-left ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <ReactMarkdown
                                  components={{
                                    h1: ({children, ...props}) => <h1 className={`text-2xl font-bold mb-3 mt-4 ${
                                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`} {...props}>{children}</h1>,
                                    h2: ({children, ...props}) => <h2 className={`text-xl font-bold mb-2 mt-3 ${
                                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                                    }`} {...props}>{children}</h2>,
                                    h3: ({children, ...props}) => <h3 className={`text-lg font-bold mb-2 mt-2 ${
                                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                                    }`} {...props}>{children}</h3>,
                                    strong: (props) => <strong className={`font-bold ${
                                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`} {...props} />,
                                    em: (props) => <em className={`italic ${
                                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`} {...props} />,
                                    ul: (props) => <ul className="list-disc list-inside my-3 space-y-2 ml-4" {...props} />,
                                    ol: (props) => <ol className="list-decimal list-inside my-3 space-y-2 ml-4" {...props} />,
                                    li: (props) => <li className="ml-2" {...props} />,
                                    code: ({className, children, ...props}) => {
                                      const isBlock = className?.includes('language-');
                                      return isBlock 
                                        ? <code className="block bg-gray-900 text-emerald-400 p-3 rounded-lg my-2 text-sm font-mono overflow-x-auto" {...props}>{children}</code>
                                        : <code className={`px-2 py-0.5 rounded text-sm font-mono ${
                                          theme === 'dark' 
                                            ? 'bg-blue-900 text-blue-300' 
                                            : 'bg-blue-200 text-blue-900'
                                        }`} {...props}>{children}</code>;
                                    },
                                    pre: ({children, ...props}) => <pre className="bg-gray-900 text-emerald-400 p-3 rounded-lg my-2 text-sm font-mono overflow-x-auto" {...props}>{children}</pre>,
                                    p: (props) => <p className="mb-3" {...props} />,
                                  }}
                                >
                                  {question.solution}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}
                          
                          {(() => {
                            const getSolutionForLanguage = () => {
                              const solutionCodeMap = {
                                java: question.solutionCodeJava,
                                python: question.solutionCodePython,
                                javascript: question.solutionCodeJavaScript,
                                cpp: question.solutionCodeCpp,
                                c: question.solutionCodeC
                              };
                              return solutionCodeMap[language] || question.solutionCode || question.solutionCodeJava;
                            };
                             
                            const currentSolution = getSolutionForLanguage();
                             
                            return currentSolution && (
                              <div className={`p-5 rounded-xl border ${
                                theme === 'dark' 
                                  ? 'bg-gray-800 border-green-700' 
                                  : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                              }`}>
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className={`text-lg font-bold flex items-center ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                                  }`}>
                                    <span className="text-2xl mr-2">💻</span>
                                    Solution Code ({language.toUpperCase()})
                                  </h4>
                                  <div className="flex gap-2">
                                    {['java', 'python', 'javascript', 'cpp', 'c'].map((lang) => {
                                      const hasSolution = lang === 'java' ? (question.solutionCodeJava || question.solutionCode) :
                                                          lang === 'python' ? question.solutionCodePython :
                                                          lang === 'javascript' ? question.solutionCodeJavaScript :
                                                          lang === 'cpp' ? question.solutionCodeCpp :
                                                          lang === 'c' ? question.solutionCodeC : false;
                                      return hasSolution && (
                                        <button
                                          key={lang}
                                          onClick={() => setLanguage(lang)}
                                          className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                                            language === lang
                                              ? 'bg-green-600 text-white shadow-md'
                                              : theme === 'dark'
                                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                : 'bg-white text-gray-600 hover:bg-green-100'
                                          }`}
                                        >
                                          {lang === 'javascript' ? 'JS' : lang === 'cpp' ? 'C++' : lang.toUpperCase()}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="bg-gray-900 p-4 rounded-lg overflow-x-auto">
                                  <pre className="text-sm font-mono text-emerald-400 whitespace-pre-wrap">
                                    {currentSolution}
                                  </pre>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      ) : (
                        <div className={`text-center py-8 rounded-lg ${
                          theme === 'dark' 
                            ? 'text-gray-400 bg-gray-800' 
                            : 'text-gray-500 bg-gradient-to-r from-gray-50 to-gray-100'
                        }`}>
                          <div className="text-4xl mb-2">📝</div>
                          <p>No solution available for this question</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Panel - Editor */}
        <div className={`flex-1 flex flex-col backdrop-blur-sm shadow-xl w-full sm:w-auto ${isMobile ? 'overflow-visible' : 'overflow-hidden'} ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white/90'
        }`} style={{ minWidth: isMobile ? '100%' : '400px', minHeight: isMobile ? 'auto' : 0, flexShrink: isMobile ? 0 : 1 }}>
          {/* Editor Toolbar */}
          <div className={`border-b px-3 sm:px-4 py-2 sm:py-2.5 ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                <span className={`text-xs sm:text-sm font-medium ${
                  theme === 'dark' ? 'text-purple-400' : 'text-purple-700'
                }`}>Editor</span>
                <span className={`px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap ${
                  theme === 'dark' 
                    ? 'bg-purple-900 text-purple-300' 
                    : 'bg-purple-100 text-purple-700'
                }`}>{language}</span>
              </div>
              
              {/* Main Action Buttons - Same Line */}
              <div className="flex items-center gap-2 sm:gap-1.5 sm:gap-2 overflow-x-auto flex-1 sm:flex-initial" style={{ WebkitOverflowScrolling: 'touch' }}>
                <button
                  data-tour="reset"
                  onClick={resetCode}
                  className={`px-4 sm:px-3 py-2.5 sm:py-1 text-sm sm:text-xs rounded hover:opacity-80 font-medium min-h-[48px] sm:min-h-0 whitespace-nowrap flex-shrink-0 flex-1 sm:flex-initial ${
                    theme === 'dark' 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Reset
                </button>
                <button
                  data-tour="run-code"
                  onClick={runCode}
                  disabled={isRunning}
                  className="px-4 sm:px-3 py-2.5 sm:py-1 text-sm sm:text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 font-medium shadow-md min-h-[48px] sm:min-h-0 whitespace-nowrap flex-shrink-0 flex-1 sm:flex-initial"
                >
                  <span className="hidden sm:inline">Run Code</span>
                  <span className="sm:hidden">Run</span>
                </button>
                {/* Check Quality Button - Hidden on mobile */}
                {!isMobile && (
                  <button
                    data-tour="code-quality"
                    onClick={checkCodeQuality}
                    disabled={isRunning || isAnalyzingCode}
                    className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 font-medium shadow-md min-h-[44px] sm:min-h-0"
                  >
                    <span className="hidden sm:inline">🔍 {isAnalyzingCode ? 'Analyzing...' : 'Check Quality'}</span>
                    <span className="sm:hidden">🔍 {isAnalyzingCode ? '...' : 'Quality'}</span>
                  </button>
                )}
                
                {/* Ghost Coding Buttons - Hidden on mobile */}
                {!isMobile && (
                  <>
                    {!isRecording ? (
                      <button
                        data-tour="record"
                        onClick={startRecording}
                        disabled={isRunning || isPlayingBack}
                        className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs bg-gradient-to-r from-red-500 to-pink-500 text-white rounded hover:from-red-600 hover:to-pink-600 disabled:opacity-50 font-medium shadow-md min-h-[44px] sm:min-h-0"
                      >
                        <span className="hidden sm:inline">🔴 Record</span>
                        <span className="sm:hidden">🔴</span>
                      </button>
                    ) : (
                      <button
                        data-tour="record"
                        onClick={stopRecording}
                        className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs bg-gradient-to-r from-red-500 to-pink-500 text-white rounded animate-pulse font-medium shadow-md min-h-[44px] sm:min-h-0"
                      >
                        <span className="hidden sm:inline">⏹️ Stop Recording</span>
                        <span className="sm:hidden">⏹️</span>
                      </button>
                    )}
                    
                    <button
                      data-tour="solution-recordings"
                      onClick={loadRecordings}
                      disabled={isRecording || isPlayingBack}
                      className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 font-medium shadow-md min-h-[44px] sm:min-h-0"
                    >
                      <span className="hidden sm:inline">👻 Solution Recordings</span>
                      <span className="sm:hidden">👻</span>
                    </button>
                  </>
                )}
                
                {/* AI Tools Dropdown */}
                <div className="ai-tools-dropdown flex-shrink-0 flex-1 sm:flex-initial" style={{ position: 'relative', zIndex: 100000 }}>
                  <button
                    ref={aiMenuButtonRef}
                    data-tour="ai-tools"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('AI Menu button clicked, current state:', showAIMenu);
                      setShowAIMenu(!showAIMenu);
                    }}
                    className="px-4 sm:px-3 py-2.5 sm:py-1 text-sm sm:text-xs bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white rounded hover:from-purple-600 hover:via-pink-600 hover:to-rose-600 font-medium shadow-md flex items-center justify-center space-x-1 min-h-[48px] sm:min-h-0 whitespace-nowrap w-full sm:w-auto"
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative', zIndex: 100000 }}
                  >
                    <span className="hidden sm:inline">🤖 AI Tools</span>
                    <span className="sm:hidden">🤖 AI</span>
                    <span style={{ fontSize: '10px' }} className="hidden sm:inline">{showAIMenu ? '▲' : '▼'}</span>
                  </button>
                  
                  {showAIMenu && createPortal(
                    <>
                      {/* Backdrop to close menu on mobile */}
                      {isMobile && (
                        <div
                          onClick={() => setShowAIMenu(false)}
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 999998,
                            background: 'rgba(0, 0, 0, 0.3)'
                          }}
                        />
                      )}
                      <div 
                        ref={(el) => {
                          aiMenuDropdownRef.current = el;
                          // Position immediately when ref is set
                          if (el && !isMobile && aiMenuButtonRef.current) {
                            const buttonRect = aiMenuButtonRef.current.getBoundingClientRect();
                            el.style.top = `${buttonRect.bottom + 8}px`;
                            el.style.left = `${buttonRect.left}px`;
                            el.style.display = 'block';
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          position: 'fixed',
                          top: isMobile ? '30%' : '0px',
                          left: isMobile ? '50%' : '0px',
                          transform: isMobile ? 'translate(-50%, -50%)' : 'none',
                          background: 'white',
                          borderRadius: '12px',
                          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                          border: '1px solid #e0e0e0',
                          width: isMobile ? '280px' : '280px',
                          maxWidth: '90vw',
                          zIndex: 999999,
                          overflow: 'hidden',
                          display: isMobile ? 'block' : 'none' // Will be set to block by ref callback
                        }}
                      >
                      {/* Header */}
                      <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '12px 16px',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>🤖</span>
                        <span>AI Learning Assistant</span>
                      </div>
                      
                      {/* Menu Items */}
                      <div style={{ padding: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!isLoadingHint) {
                              getAIHint();
                              // Don't close menu immediately - let loading indicator show
                              setTimeout(() => setShowAIMenu(false), 300);
                            }
                          }}
                          disabled={isLoadingHint || !question}
                          style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '12px',
                            textAlign: 'left',
                            background: isLoadingHint || !question ? '#f5f5f5' : 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isLoadingHint || !question ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            marginBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? '8px' : '12px',
                            fontSize: isMobile ? '12px' : '13px',
                            opacity: isLoadingHint || !question ? 0.5 : 1,
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (!isLoadingHint && question) {
                              e.target.style.background = 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)';
                              e.target.style.transform = 'translateX(4px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = isLoadingHint || !question ? '#f5f5f5' : 'white';
                            e.target.style.transform = 'translateX(0)';
                          }}
                        >
                          {isLoadingHint && (
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '8px 0 0 8px',
                              animation: 'pulse 1.5s ease-in-out infinite'
                            }} />
                          )}
                          <span style={{ fontSize: isMobile ? '20px' : '24px' }}>💡</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '12px' : '13px' }}>
                              {isLoadingHint ? 'Loading Hint...' : 'Get Hint'}
                              {isLoadingHint && (
                                <div style={{
                                  width: isMobile ? '10px' : '12px',
                                  height: isMobile ? '10px' : '12px',
                                  border: '2px solid #667eea',
                                  borderTop: '2px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 0.8s linear infinite'
                                }} />
                              )}
                            </div>
                            <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginTop: '2px' }}>
                              {isLoadingHint ? 'AI is thinking...' : `Progressive hints (Level ${hintLevel}/4)`}
                            </div>
                          </div>
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!isLoadingExplanation) {
                              explainCode();
                              setTimeout(() => setShowAIMenu(false), 300);
                            }
                          }}
                          disabled={isLoadingExplanation || !code}
                          style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '12px',
                            textAlign: 'left',
                            background: isLoadingExplanation || !code ? '#f5f5f5' : 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isLoadingExplanation || !code ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            marginBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? '8px' : '12px',
                            fontSize: isMobile ? '12px' : '13px',
                            opacity: isLoadingExplanation || !code ? 0.5 : 1,
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (!isLoadingExplanation && code) {
                              e.target.style.background = 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
                              e.target.style.transform = 'translateX(4px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = isLoadingExplanation || !code ? '#f5f5f5' : 'white';
                            e.target.style.transform = 'translateX(0)';
                          }}
                        >
                          {isLoadingExplanation && (
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '8px 0 0 8px',
                              animation: 'pulse 1.5s ease-in-out infinite'
                            }} />
                          )}
                          <span style={{ fontSize: isMobile ? '20px' : '24px' }}>🧠</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '12px' : '13px' }}>
                              {isLoadingExplanation ? 'Explaining...' : 'Explain Code'}
                              {isLoadingExplanation && (
                                <div style={{
                                  width: isMobile ? '10px' : '12px',
                                  height: isMobile ? '10px' : '12px',
                                  border: '2px solid #667eea',
                                  borderTop: '2px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 0.8s linear infinite'
                                }} />
                              )}
                            </div>
                            <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginTop: '2px' }}>
                              {isLoadingExplanation ? 'AI is analyzing...' : 'Understand what your code does'}
                            </div>
                          </div>
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!isAnalyzingComplexity) {
                              analyzeComplexity();
                              setTimeout(() => setShowAIMenu(false), 300);
                            }
                          }}
                          disabled={isAnalyzingComplexity || !code}
                          style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '12px',
                            textAlign: 'left',
                            background: isAnalyzingComplexity || !code ? '#f5f5f5' : 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isAnalyzingComplexity || !code ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? '8px' : '12px',
                            fontSize: isMobile ? '12px' : '13px',
                            opacity: isAnalyzingComplexity || !code ? 0.5 : 1,
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (!isAnalyzingComplexity && code) {
                              e.target.style.background = 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)';
                              e.target.style.transform = 'translateX(4px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = isAnalyzingComplexity || !code ? '#f5f5f5' : 'white';
                            e.target.style.transform = 'translateX(0)';
                          }}
                        >
                          {isAnalyzingComplexity && (
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '8px 0 0 8px',
                              animation: 'pulse 1.5s ease-in-out infinite'
                            }} />
                          )}
                          <span style={{ fontSize: isMobile ? '20px' : '24px' }}>📊</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '12px' : '13px' }}>
                              {isAnalyzingComplexity ? 'Analyzing...' : 'Analyze Complexity'}
                              {isAnalyzingComplexity && (
                                <div style={{
                                  width: isMobile ? '10px' : '12px',
                                  height: isMobile ? '10px' : '12px',
                                  border: '2px solid #667eea',
                                  borderTop: '2px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 0.8s linear infinite'
                                }} />
                              )}
                            </div>
                            <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginTop: '2px' }}>
                              {isAnalyzingComplexity ? 'Computing Big O...' : 'Get Big O time & space analysis'}
                            </div>
                          </div>
                        </button>
                      </div>
                      
                      {/* Footer */}
                      <div style={{
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        background: '#f9f9f9',
                        borderTop: '1px solid #e0e0e0',
                        fontSize: isMobile ? '9px' : '10px',
                        color: '#999',
                        textAlign: 'center'
                      }}>
                        Powered by AI • Learn smarter, not harder
                      </div>
                      </div>
                    </>,
                    document.body
                  )}
                </div>
              </div>
              
              {/* Submit Button - Desktop: Same line, Mobile: Separate line */}
              <div className="hidden sm:inline-block relative group">
                <button
                  data-tour="submit"
                  onClick={(e) => {
                    if (!isAuthenticated()) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    if (!isRunning) {
                      submitCode();
                    }
                  }}
                  disabled={isRunning}
                  className={`px-3 py-1 text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded hover:from-purple-600 hover:to-indigo-600 font-medium shadow-md whitespace-nowrap ${
                    !isAuthenticated() ? 'opacity-50 cursor-not-allowed' : ''
                  } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Submit
                </button>
                {!isAuthenticated() && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    Please login to submit your solution
                    <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Submit Button - Mobile: Separate line below */}
            <div className="mt-2 sm:hidden relative group">
              <button
                data-tour="submit"
                onClick={(e) => {
                  if (!isAuthenticated()) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  if (!isRunning) {
                    submitCode();
                  }
                }}
                disabled={isRunning}
                className={`w-full px-4 py-2.5 text-sm bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded hover:from-purple-600 hover:to-indigo-600 font-medium shadow-md min-h-[48px] whitespace-nowrap ${
                  !isAuthenticated() ? 'opacity-50 cursor-not-allowed' : ''
                } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Submit
              </button>
              {!isAuthenticated() && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 w-max max-w-[90vw]">
                  Please login to submit your solution
                  <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                </div>
              )}
            </div>
          </div>

          {/* Editor - Resizable height */}
          <div 
            ref={editorPanelRef}
            data-tour="editor"
            style={{ 
              height: isMobile ? '400px' : `${editorHeight}%`, 
              minHeight: isMobile ? '300px' : '200px', 
              position: 'relative', 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flexShrink: 0,
              willChange: isVerticalResizing ? 'height' : 'auto',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden'
            }}
            className="codemirror-editor-container"
          >
            <div ref={editorContainerRef} style={{ flex: 1, minHeight: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
              <CodeMirror
                key={`editor-${question?.id || 'default'}-${language}-${theme}`}
                value={code}
                height="100%"
                theme={theme === 'dark' ? oneDark : basicLight}
                extensions={[getLanguageExtension(language)]}
                onChange={(value) => {
                  setCode(value);
                }}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: false,
                  dropCursor: false,
                  allowMultipleSelections: false,
                  indentOnInput: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: false,
                  highlightSelectionMatches: false,
                }}
                style={{ 
                  height: '100%', 
                  width: '100%',
                  // Prevent CodeMirror from recalculating during resize
                  pointerEvents: isVerticalResizing ? 'none' : 'auto'
                }}
              />
            </div>
          </div>

          {/* Vertical Resize Handle - Hide on mobile */}
          {!isMobile && (
            <div
              ref={verticalResizeRef}
              onMouseDown={() => setIsVerticalResizing(true)}
              className={`w-full cursor-row-resize transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-500 hover:bg-gray-400' 
                  : 'bg-purple-400 hover:bg-purple-600'
              }`}
              style={{ 
                userSelect: 'none',
                height: '2px',
                width: '100%',
                willChange: 'background-color',
                transform: 'translateZ(0)',
                flexShrink: 0,
                position: 'relative',
                minHeight: '2px',
                zIndex: 30
              }}
              title="Drag to resize console"
            />
          )}

          {/* Output Panel - Resizable height */}
          <div 
            ref={outputPanelRef}
            className={`overflow-hidden ${
              theme === 'dark' 
                ? 'bg-gray-800' 
                : 'bg-gradient-to-r from-gray-50 to-gray-100'
            }`} style={{ 
              height: isMobile ? '300px' : `${100 - editorHeight}%`, 
              minHeight: isMobile ? '200px' : '150px', 
              flexShrink: 0,
              willChange: isVerticalResizing ? 'height' : 'auto',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden'
            }}>
            <div className="h-full flex flex-col p-2 sm:p-3" style={{ minHeight: 0, overflow: 'hidden' }}>
              {/* Header with Custom Input */}
              <div className="mb-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap">
                    <h3 className={`text-xs sm:text-sm font-semibold flex items-center ${
                      theme === 'dark' ? 'text-white' : 'text-gray-800'
                    }`}>
                      <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                      Console Output
                    </h3>
                    <label className="flex items-center cursor-pointer min-h-[44px] sm:min-h-0">
                      <input
                        type="checkbox"
                        checked={useCustomInput}
                        onChange={(e) => setUseCustomInput(e.target.checked)}
                        className="mr-1.5 cursor-pointer w-4 h-4 sm:w-auto sm:h-auto"
                      />
                      <span className={`text-xs font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>Use Custom Input</span>
                      {useCustomInput && (
                        <span className={`ml-1 text-xs ${
                          theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                        }`}>▼</span>
                      )}
                    </label>
                  </div>
                  <button
                    onClick={() => setOutput('')}
                    className={`text-xs font-medium min-h-[44px] sm:min-h-0 px-2 sm:px-0 ${
                      theme === 'dark' 
                        ? 'text-purple-400 hover:text-purple-300' 
                        : 'text-purple-600 hover:text-purple-800'
                    }`}
                  >
                    Clear
                  </button>
                </div>
                
                {/* Expandable Custom Input Textarea */}
                {useCustomInput && (
                  <div className={`mt-2 border rounded-lg p-2 sm:p-2 shadow-sm ${
                    theme === 'dark' 
                      ? 'bg-gray-900 border-purple-700' 
                      : 'bg-white border-purple-300'
                  }`}>
                    <label className={`text-xs font-medium mb-1 block ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>Custom Input:</label>
                    <textarea
                      value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      placeholder="Enter your custom input here&#10;&#10;Examples:&#10;• Two Sum: [2,7,11,15], 9&#10;• Valid Parentheses: ()[]{}&#10;• Multiple lines supported&#10;&#10;Press Ctrl+Enter to run"
                      className={`w-full px-2 py-1.5 text-xs sm:text-sm border rounded focus:outline-none focus:ring-2 font-mono resize-y ${
                        theme === 'dark' 
                          ? 'bg-gray-950 border-gray-700 text-white focus:ring-purple-500' 
                          : 'border-purple-200 focus:ring-purple-400'
                      }`}
                      rows="3"
                      onKeyDown={(e) => {
                        if (e.ctrlKey && e.key === 'Enter') {
                          runCode();
                        }
                      }}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
                      <span className={`text-xs italic ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>Tip: Press Ctrl+Enter to run with this input</span>
                      <button
                        onClick={() => {
                          setCustomInput('');
                          setUseCustomInput(false);
                        }}
                        className={`text-xs font-medium min-h-[44px] sm:min-h-0 px-2 sm:px-0 ${
                          theme === 'dark' 
                            ? 'text-red-400 hover:text-red-300' 
                            : 'text-red-600 hover:text-red-800'
                        }`}
                      >
                        Clear & Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Output Display - Scrollable */}
              <div 
                className={`flex-1 ide-console-output border rounded-lg p-2 sm:p-3 overflow-y-auto shadow-sm ${
                  theme === 'dark' 
                    ? 'bg-gray-900 border-gray-700' 
                    : 'bg-white border-purple-200'
                }`}
                style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}
              >
                <pre 
                  className="text-xs sm:text-sm whitespace-pre-wrap break-words"
                  style={{
                    backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                    color: theme === 'dark' ? '#e5e7eb' : '#111827',
                    margin: 0,
                    padding: 0,
                  }}
                >
                  {output || '💡 No output yet. Click "Run Code" to execute.\n\nTip: Add custom input above and press Ctrl+Enter to test.'}
                </pre>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Solution Warning Modal */}
      {showSolutionWarning && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full sm:mx-4 overflow-hidden shadow-2xl border border-yellow-300 max-h-[90vh] sm:max-h-[85vh] flex flex-col">
            <div className="p-4 sm:p-6 border-b border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
                <span className="text-2xl sm:text-3xl mr-2 sm:mr-3">⚠️</span>
                Warning: XP Will Be Forfeited
              </h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <p className="text-sm sm:text-base text-gray-700">
                If you view the solution for this question, you will <strong>not receive any XP</strong> when you solve it.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-yellow-800">
                  <strong>💡 Tip:</strong> Try solving the problem on your own first! Use hints if you need help. 
                  You can always view the solution later after attempting the problem.
                </p>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm">
                Are you sure you want to view the solution?
              </p>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-3">
              <button
                onClick={() => setShowSolutionWarning(false)}
                className="px-4 sm:px-5 py-2.5 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium min-h-[44px] sm:min-h-0"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Record solution view
                  if (question?.id && isAuthenticated()) {
                    try {
                      await api.post(`/api/ide/solution-view/${question.id}`);
                      setHasViewedSolution(true);
                    } catch (error) {
                      console.error('Failed to record solution view:', error);
                    }
                  }
                  setShowSolutionWarning(false);
                  setShowSolution(true);
                }}
                className="px-4 sm:px-5 py-2.5 sm:py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 font-medium min-h-[44px] sm:min-h-0"
              >
                View Solution Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Solution Modal */}
      {showSolution && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-5xl max-h-[90vh] sm:max-h-[85vh] w-full sm:mx-4 overflow-hidden shadow-2xl border border-purple-200 flex flex-col">
            <div className="p-4 sm:p-6 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg sm:text-2xl font-bold text-gray-800 flex items-center">
                <span className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full mr-2 sm:mr-3"></span>
                💡 Solution Explanation
              </h3>
              <button
                onClick={() => setShowSolution(false)}
                className="text-purple-500 hover:text-purple-700 text-2xl sm:text-3xl font-bold hover:bg-purple-100 rounded-full w-10 h-10 flex items-center justify-center transition-all min-h-[44px] sm:min-h-0"
              >
                ×
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4 sm:space-y-6">
              {question.solution || question.solutionCode ? (
                <div className="space-y-6">
                  
                  {/* Solution Explanation */}
                  {question.solution && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
                      <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                        <span className="text-2xl mr-2">📖</span>
                        Explanation
                      </h4>
                      <div className="text-gray-700 leading-relaxed text-left">
                        <ReactMarkdown
                          components={{
                            h1: ({children, ...props}) => <h1 className="text-2xl font-bold mb-3 mt-4 text-gray-900" {...props}>{children}</h1>,
                            h2: ({children, ...props}) => <h2 className="text-xl font-bold mb-2 mt-3 text-gray-800" {...props}>{children}</h2>,
                            h3: ({children, ...props}) => <h3 className="text-lg font-bold mb-2 mt-2 text-gray-800" {...props}>{children}</h3>,
                            strong: (props) => <strong className="font-bold text-gray-900" {...props} />,
                            em: (props) => <em className="italic text-gray-700" {...props} />,
                            ul: (props) => <ul className="list-disc list-inside my-3 space-y-2 ml-4" {...props} />,
                            ol: (props) => <ol className="list-decimal list-inside my-3 space-y-2 ml-4" {...props} />,
                            li: (props) => <li className="ml-2" {...props} />,
                            code: ({className, children, ...props}) => {
                              const isBlock = className?.includes('language-');
                              return isBlock 
                                ? <code className="block bg-gray-800 text-gray-100 p-3 rounded-lg my-2 text-sm font-mono overflow-x-auto" {...props}>{children}</code>
                                : <code className="bg-blue-200 px-2 py-0.5 rounded text-sm font-mono text-blue-900" {...props}>{children}</code>;
                            },
                            pre: ({children, ...props}) => <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg my-2 text-sm font-mono overflow-x-auto" {...props}>{children}</pre>,
                            p: (props) => <p className="mb-3" {...props} />,
                          }}
                        >
                          {question.solution}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                  
                  {/* Solution Code */}
                  {(() => {
                    // Get solution code for current language
                    const getSolutionForLanguage = () => {
                      const solutionCodeMap = {
                        java: question.solutionCodeJava,
                        python: question.solutionCodePython,
                        javascript: question.solutionCodeJavaScript,
                        cpp: question.solutionCodeCpp,
                        c: question.solutionCodeC
                      };
                      return solutionCodeMap[language] || question.solutionCode || question.solutionCodeJava;
                    };
                    
                    const currentSolution = getSolutionForLanguage();
                    
                    return currentSolution && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-lg font-bold text-gray-800 flex items-center">
                            <span className="text-2xl mr-2">💻</span>
                            Solution Code ({language.toUpperCase()})
                          </h4>
                          <div className="flex gap-2">
                            {['java', 'python', 'javascript', 'cpp', 'c'].map((lang) => {
                              const hasSolution = lang === 'java' ? (question.solutionCodeJava || question.solutionCode) :
                                                  lang === 'python' ? question.solutionCodePython :
                                                  lang === 'javascript' ? question.solutionCodeJavaScript :
                                                  lang === 'cpp' ? question.solutionCodeCpp :
                                                  lang === 'c' ? question.solutionCodeC : false;
                              return hasSolution && (
                                <button
                                  key={lang}
                                  onClick={() => setLanguage(lang)}
                                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                                    language === lang
                                      ? 'bg-green-600 text-white shadow-md'
                                      : 'bg-white text-gray-600 hover:bg-green-100'
                                  }`}
                                >
                                  {lang === 'javascript' ? 'JS' : lang === 'cpp' ? 'C++' : lang.toUpperCase()}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="bg-gray-900 p-4 rounded-lg overflow-x-auto">
                          <pre className="text-sm text-green-400 font-mono leading-relaxed text-left whitespace-pre">
                            {currentSolution}
                          </pre>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Video Link */}
                  {question.videoLink && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-200">
                      <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                        <span className="text-2xl mr-2">🎥</span>
                        Video Tutorial
                      </h4>
                      <a
                        href={question.videoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold shadow-lg hover:shadow-xl"
                      >
                        <span className="text-xl">▶️</span>
                        Watch Video Explanation
                      </a>
                    </div>
                  )}
                  
                  {/* Follow-up Questions */}
                  {question.followUp && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-5 rounded-xl border border-yellow-200">
                      <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                        <span className="text-2xl mr-2">🤔</span>
                        Follow-up Questions
                      </h4>
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-left">
                        {question.followUp}
                      </div>
                    </div>
                  )}
                  
                  {/* Related Topics */}
                  {question.relatedTopics && (
                    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-5 rounded-xl border border-cyan-200">
                      <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                        <span className="text-2xl mr-2">🔗</span>
                        Related Topics
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {question.relatedTopics.split(',').map((topic, index) => (
                          <span
                            key={index}
                            className="bg-white px-3 py-1 rounded-full text-sm font-medium text-cyan-700 border border-cyan-300"
                          >
                            {topic.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                  <div className="text-6xl mb-3">💡</div>
                  <p className="text-xl font-semibold">No solution available for this question</p>
                  <p className="text-sm mt-2">Try solving it yourself first!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Code Quality Modal */}
      {showCodeQuality && codeQualityResults && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCodeQuality(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-yellow-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-2xl">
                    🔍
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Code Quality Report</h3>
                    <p className="text-sm text-gray-600 mt-1">{codeQualityResults.rating || 'Analysis Complete'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${
                      codeQualityResults.score > 80 ? 'text-green-600' : 
                      codeQualityResults.score > 60 ? 'text-yellow-600' : 
                      'text-red-600'
                    }`}>
                      {Math.round(codeQualityResults.score)}
                    </div>
                    <div className="text-xs text-gray-500 font-medium">out of 100</div>
                  </div>
                  <button
                    onClick={() => setShowCodeQuality(false)}
                    className="text-gray-500 hover:text-gray-700 text-3xl font-bold hover:bg-yellow-100 rounded-full w-10 h-10 flex items-center justify-center transition-all"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              {codeQualityResults.smells && codeQualityResults.smells.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">⚠️</span>
                    Issues Found ({codeQualityResults.smells.length})
                  </h4>
                  
                  {codeQualityResults.smells.map((smell, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border-l-4 shadow-sm ${
                        smell.severity === 'critical' ? 'bg-red-50 border-red-500' :
                        smell.severity === 'major' ? 'bg-orange-50 border-orange-500' :
                        smell.severity === 'minor' ? 'bg-yellow-50 border-yellow-500' :
                        'bg-blue-50 border-blue-500'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-1 text-xs font-bold rounded uppercase ${
                              smell.severity === 'critical' ? 'bg-red-200 text-red-800' :
                              smell.severity === 'major' ? 'bg-orange-200 text-orange-800' :
                              smell.severity === 'minor' ? 'bg-yellow-200 text-yellow-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {smell.severity}
                            </span>
                            <h5 className="font-bold text-gray-800">{smell.type}</h5>
                          </div>
                          
                          <p className="text-gray-700 mb-2">{smell.description}</p>
                          
                          {smell.fix && (
                            <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                              <div className="text-xs font-semibold text-gray-600 mb-1">💡 How to fix:</div>
                              <div className="text-sm text-gray-700">{smell.fix}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">✨</div>
                  <h4 className="text-2xl font-bold text-green-600 mb-2">Excellent Code!</h4>
                  <p className="text-gray-600">No code smells detected. Keep up the great work!</p>
                  <div className="mt-6 inline-flex items-center space-x-2 px-6 py-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-green-600 font-semibold">Score: {Math.round(codeQualityResults.score)}/100</span>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {codeQualityResults.suggestions && codeQualityResults.suggestions.length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">💡</span>
                    Quick Suggestions
                  </h4>
                  <ul className="space-y-2">
                    {codeQualityResults.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ghost Coding Recordings Modal */}
      {showRecordings && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowRecordings(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-cyan-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center text-2xl">
                    👻
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Ghost Coding Sessions</h3>
                    <p className="text-sm text-gray-600 mt-1">Watch how experts solved this problem</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRecordings(false)}
                  className="text-gray-500 hover:text-gray-700 text-3xl font-bold hover:bg-cyan-100 rounded-full w-10 h-10 flex items-center justify-center transition-all"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              {availableRecordings.length > 0 ? (
                <div className="space-y-6">
                  {/* Featured Recordings Section */}
                  {availableRecordings.some(r => r.isFeaturedSection) && (
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-2xl">⭐</span>
                        <h4 className="text-lg font-bold text-gray-800">Featured Recordings</h4>
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
                          Admin Featured
                        </span>
                      </div>
                      <div className="space-y-3">
                        {availableRecordings
                          .filter(r => r.isFeaturedSection)
                          .map((recording) => (
                            <div
                              key={recording.sessionId || recording.id}
                              className="p-4 rounded-lg border-2 border-yellow-300 hover:border-yellow-500 bg-yellow-50 hover:bg-yellow-100 transition-all cursor-pointer"
                              onClick={() => playRecording(recording)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold">
                                      {recording.userName ? recording.userName[0].toUpperCase() : '⭐'}
                                    </div>
                                    <div>
                                      <h4 className="font-bold text-gray-800">{recording.userName || 'Expert'}</h4>
                                      <p className="text-xs text-gray-600">
                                        {recording.language} • {recording.createdAt ? new Date(recording.createdAt).toLocaleDateString() : 'Recent'}
                                      </p>
                                    </div>
                                    <span className="text-yellow-500 text-xl">⭐</span>
                                  </div>
                                  <p className="text-sm text-gray-700 ml-13">
                                    {recording.duration ? `Duration: ${Math.ceil(recording.duration / 60)} minutes` : 'Session recording'}
                                  </p>
                                </div>
                                <button className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium">
                                  ▶️ Play
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* My Recordings Section */}
                  {availableRecordings.some(r => r.isMyRecording) && (
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-2xl">👤</span>
                        <h4 className="text-lg font-bold text-gray-800">My Recordings</h4>
                        <span className="bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full text-xs font-semibold">
                          Your Sessions
                        </span>
                      </div>
                      <div className="space-y-3">
                        {availableRecordings
                          .filter(r => r.isMyRecording)
                          .map((recording) => (
                            <div
                              key={recording.sessionId || recording.id}
                              className="p-4 rounded-lg border-2 border-cyan-200 hover:border-cyan-400 bg-cyan-50 hover:bg-cyan-100 transition-all cursor-pointer"
                              onClick={() => playRecording(recording)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                                      {recording.userName ? recording.userName[0].toUpperCase() : 'M'}
                                    </div>
                                    <div>
                                      <h4 className="font-bold text-gray-800">{recording.userName || 'Me'}</h4>
                                      <p className="text-xs text-gray-600">
                                        {recording.language} • {recording.createdAt ? new Date(recording.createdAt).toLocaleDateString() : 'Recent'}
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-700 ml-13">
                                    {recording.duration ? `Duration: ${Math.ceil(recording.duration / 60)} minutes` : 'Session recording'}
                                  </p>
                                </div>
                                <button className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 font-medium">
                                  ▶️ Play
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👻</div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">No Recordings Yet</h4>
                  <p className="text-gray-600">Be the first to record a session for this problem!</p>
                  <button
                    onClick={() => {
                      setShowRecordings(false);
                      startRecording();
                    }}
                    className="mt-6 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium inline-flex items-center space-x-2"
                  >
                    <span>🔴</span>
                    <span>Start Recording</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Playback Progress Indicator */}
      {isPlayingBack && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-cyan-500 text-white px-6 py-3 rounded-full shadow-2xl z-50">
          <div className="flex items-center space-x-3">
            <div className="animate-pulse text-xl">👻</div>
            <div>
              <div className="text-sm font-bold">Playing Back Session</div>
              <div className="text-xs">Progress: {Math.round(playbackProgress)}%</div>
            </div>
          </div>
        </div>
      )}

      {/* AI Hint Modal */}
      {showAIHint && aiHint && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAIHint(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-green-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                    💡
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">AI Hint</h3>
                    <p className="text-sm text-green-100 mt-1">Level {hintLevel} of 4</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAIHint(false)}
                  className="text-white hover:text-green-200 text-3xl font-bold hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              <div className="prose max-w-none">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg mb-4 text-left">
                  <ReactMarkdown
                    components={{
                      h1: ({children, ...props}) => <h1 className="text-xl font-bold mb-3 text-gray-800" {...props}>{children}</h1>,
                      h2: ({children, ...props}) => <h2 className="text-lg font-bold mb-2 text-gray-800" {...props}>{children}</h2>,
                      h3: ({children, ...props}) => <h3 className="text-base font-bold mb-2 text-gray-700" {...props}>{children}</h3>,
                      strong: (props) => <strong className="font-bold text-gray-900" {...props} />,
                      em: (props) => <em className="italic text-gray-700" {...props} />,
                      ul: (props) => <ul className="list-disc list-inside my-2 space-y-1 ml-2 text-left" {...props} />,
                      ol: (props) => <ol className="list-decimal list-inside my-2 space-y-1 ml-2 text-left" {...props} />,
                      li: (props) => <li className="ml-2 leading-relaxed text-left" {...props} />,
                      p: (props) => <p className="mb-2 text-gray-800 text-left" {...props} />,
                      code: ({className, children, ...props}) => {
                        const isBlock = className?.includes('language-');
                        return isBlock 
                          ? <code className="block bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-sm font-mono my-2" {...props}>{children}</code>
                          : <code className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>;
                      },
                      pre: ({children, ...props}) => <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono my-3" {...props}>{children}</pre>
                    }}
                  >
                    {aiHint.hint}
                  </ReactMarkdown>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
              {hintLevel > 1 && (
                <button
                  onClick={() => {
                    setHintLevel(hintLevel - 1);
                    setShowAIHint(false);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  ← Previous Hint
                </button>
              )}
              <div className="flex-1"></div>
              {aiHint.nextLevel && (
                <button
                  onClick={() => {
                    setHintLevel(aiHint.nextLevel);
                    setShowAIHint(false);
                    setTimeout(() => getAIHint(), 100);
                  }}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                >
                  Next Hint →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Code Explainer Modal */}
      {showAIExplainer && aiExplanation && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAIExplainer(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-purple-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                    🤖
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">AI Code Explanation</h3>
                    <p className="text-sm text-purple-100 mt-1">Understanding your code</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAIExplainer(false)}
                  className="text-white hover:text-purple-200 text-3xl font-bold hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              {/* Show selected code */}
              {selectedCode && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Selected Code:</h4>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{selectedCode}</code>
                  </pre>
                </div>
              )}

              {/* AI Explanation */}
              <div className="prose max-w-none">
                <h4 className="font-semibold text-gray-700 mb-2">Explanation:</h4>
                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg text-left">
                  <ReactMarkdown
                    components={{
                      h1: ({children, ...props}) => <h1 className="text-xl font-bold mb-3 text-gray-800" {...props}>{children}</h1>,
                      h2: ({children, ...props}) => <h2 className="text-lg font-bold mb-2 text-gray-800" {...props}>{children}</h2>,
                      h3: ({children, ...props}) => <h3 className="text-base font-bold mb-2 text-gray-700" {...props}>{children}</h3>,
                      strong: (props) => <strong className="font-bold text-gray-900" {...props} />,
                      em: (props) => <em className="italic text-gray-700" {...props} />,
                      ul: (props) => <ul className="list-disc list-inside my-2 space-y-1 ml-2 text-left" {...props} />,
                      ol: (props) => <ol className="list-decimal list-inside my-2 space-y-1 ml-2 text-left" {...props} />,
                      li: (props) => <li className="ml-2 leading-relaxed text-left" {...props} />,
                      p: (props) => <p className="mb-2 text-gray-800 text-left" {...props} />,
                      code: ({className, children, ...props}) => {
                        const isBlock = className?.includes('language-');
                        return isBlock 
                          ? <code className="block bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-sm font-mono my-2" {...props}>{children}</code>
                          : <code className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>;
                      },
                      pre: ({children, ...props}) => <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono my-3" {...props}>{children}</pre>
                    }}
                  >
                    {aiExplanation}
                  </ReactMarkdown>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t">
              <button
                onClick={() => setShowAIExplainer(false)}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Complexity Analyzer Modal */}
      {showComplexityAnalysis && complexityAnalysis && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowComplexityAnalysis(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-pink-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                    📊
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Complexity Analysis</h3>
                    <p className="text-sm text-pink-100 mt-1">Time & Space Complexity Breakdown</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowComplexityAnalysis(false)}
                  className="text-white hover:text-pink-200 text-3xl font-bold hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              {/* Show code */}
              <div className="mb-4">
                <h4 className="font-semibold text-gray-700 mb-2">Analyzed Code:</h4>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm max-h-48">
                  <code>{code}</code>
                </pre>
              </div>

              {/* AI Analysis */}
              <div className="prose max-w-none">
                <h4 className="font-semibold text-gray-700 mb-2">Analysis:</h4>
                <div className="bg-pink-50 border-l-4 border-pink-500 p-4 rounded-r-lg text-left">
                  <ReactMarkdown
                    components={{
                      h1: ({children, ...props}) => <h1 className="text-xl font-bold mb-3 text-gray-800" {...props}>{children}</h1>,
                      h2: ({children, ...props}) => <h2 className="text-lg font-bold mb-2 text-gray-800" {...props}>{children}</h2>,
                      h3: ({children, ...props}) => <h3 className="text-base font-bold mb-2 text-gray-700" {...props}>{children}</h3>,
                      strong: (props) => <strong className="font-bold text-gray-900" {...props} />,
                      em: (props) => <em className="italic text-gray-700" {...props} />,
                      ul: (props) => <ul className="list-disc list-inside my-2 space-y-1 ml-2 text-left" {...props} />,
                      ol: (props) => <ol className="list-decimal list-inside my-2 space-y-1 ml-2 text-left" {...props} />,
                      li: (props) => <li className="ml-2 leading-relaxed text-left" {...props} />,
                      p: (props) => <p className="mb-2 text-gray-800 text-left" {...props} />,
                      code: ({className, children, ...props}) => {
                        const isBlock = className?.includes('language-');
                        return isBlock 
                          ? <code className="block bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-sm font-mono my-2" {...props}>{children}</code>
                          : <code className="bg-pink-100 text-pink-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>;
                      },
                      pre: ({children, ...props}) => <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono my-3" {...props}>{children}</pre>
                    }}
                  >
                    {complexityAnalysis}
                  </ReactMarkdown>
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">💡</div>
                  <div>
                    <h5 className="font-semibold text-blue-900 mb-1">Understanding Big O Notation</h5>
                    <p className="text-sm text-blue-800">
                      Big O describes how runtime/space grows with input size. Common complexities: O(1) constant, 
                      O(log n) logarithmic, O(n) linear, O(n log n) linearithmic, O(n²) quadratic, O(2ⁿ) exponential.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t">
              <button
                onClick={() => setShowComplexityAnalysis(false)}
                className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 font-medium"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Loading Toast Notification */}
      {aiLoadingMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '16px 24px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
            animation: 'slideInUp 0.3s ease-out',
            zIndex: 9999,
            maxWidth: '400px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(255,255,255,0.3)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          ></div>
          <div style={{ flex: 1 }}>
            <div style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '2px'
            }}>
              Loading AI Feature
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: '12px',
              fontWeight: '400'
            }}>
              {aiLoadingMessage}
            </div>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes slideInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </div>
      )}

      {/* Login Required Modal */}
      {showLoginPrompt && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4"
          onClick={() => setShowLoginPrompt(false)}
        >
          <div 
            className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-md w-full border-2 border-cyan-400/50 shadow-2xl shadow-cyan-500/30"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🔐</div>
              <h3 className="text-3xl font-bold text-white mb-3">Login Required</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                <span className="text-cyan-400 font-bold">{loginPromptFeature}</span> is a premium AI feature available for registered users only.
              </p>
            </div>

            <div className="bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-400/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-3xl">🤖</span>
                <div>
                  <p className="text-cyan-300 text-sm font-bold mb-2">AI-Powered Features</p>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Intelligent code hints</li>
                    <li>• Step-by-step explanations</li>
                    <li>• Ghost coding playback</li>
                    <li>• Progress tracking & XP</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  setShowAuthModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 shadow-lg hover:shadow-cyan-500/50 transform hover:scale-105"
              >
                🚀 Login Now
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-bold text-base transition-all duration-300"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission View Modal */}
      {showSubmissionModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Submission Details</h3>
                <p className="text-purple-100 text-sm mt-1">
                  {new Date(selectedSubmission.timestamp).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSubmissionModal(false);
                  setSelectedSubmission(null);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-2xl transition-all duration-200 hover:rotate-90"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              {/* Status and Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                  <div className="text-xs text-purple-600 font-semibold mb-1">STATUS</div>
                  <div className={`text-lg font-bold ${
                    selectedSubmission.status === 'ACCEPTED' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {selectedSubmission.status === 'ACCEPTED' ? '✅ Accepted' : '❌ Wrong'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                  <div className="text-xs text-blue-600 font-semibold mb-1">LANGUAGE</div>
                  <div className="text-lg font-bold text-blue-900">{selectedSubmission.language}</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                  <div className="text-xs text-green-600 font-semibold mb-1">TESTS PASSED</div>
                  <div className="text-lg font-bold text-green-900">
                    {selectedSubmission.passedTests}/{selectedSubmission.totalTests}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
                  <div className="text-xs text-orange-600 font-semibold mb-1">RUNTIME</div>
                  <div className="text-lg font-bold text-orange-900">
                    {selectedSubmission.executionTime || 'N/A'}{selectedSubmission.executionTime ? 'ms' : ''}
                  </div>
                </div>
              </div>

              {/* Code Display */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-bold text-gray-900">Submitted Code</h4>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedSubmission.code);
                      alert('Code copied to clipboard!');
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg font-medium transition-colors"
                  >
                    📋 Copy Code
                  </button>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-sm text-gray-100 font-mono leading-relaxed text-left m-0" style={{ whiteSpace: 'pre', textAlign: 'left' }}>
                    <code>{selectedSubmission.code}</code>
                  </pre>
                </div>
              </div>

              {/* Test Results */}
              {selectedSubmission.testResults && selectedSubmission.testResults.length > 0 && (
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Test Results</h4>
                  <div className="space-y-2">
                    {selectedSubmission.testResults.map((result, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border-2 ${
                          result.passed
                            ? 'bg-green-50 border-green-300'
                            : 'bg-red-50 border-red-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900">Test Case #{index + 1}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            result.passed ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'
                          }`}>
                            {result.passed ? '✅ PASSED' : '❌ FAILED'}
                          </span>
                        </div>
                        {result.input && (
                          <div className="text-xs mb-2">
                            <strong className="block mb-1 text-gray-800">Input:</strong>
                            <pre className="bg-white p-2 rounded text-xs font-mono overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap break-words border border-gray-200">
                              {result.input}
                            </pre>
                          </div>
                        )}
                        {result.expected && (
                          <div className="text-xs mb-2">
                            <strong className="block mb-1 text-gray-800">Expected:</strong>
                            <pre className="bg-white p-2 rounded text-xs font-mono overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap break-words border border-gray-200">
                              {result.expected}
                            </pre>
                          </div>
                        )}
                        {result.actual && (
                          <div className="text-xs">
                            <strong className="block mb-1 text-gray-800">Actual:</strong>
                            <pre className="bg-white p-2 rounded text-xs font-mono overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap break-words border border-gray-200">
                              {result.actual}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowSubmissionModal(false);
                  setSelectedSubmission(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Success Modal */}
      {showSuccessModal && submissionResults && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
          onClick={() => setShowSuccessModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 transform transition-all"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideIn 0.3s ease-out',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-1">
              🎉 Congratulations!
            </h2>
            <p className="text-center text-gray-600 text-sm mb-4">
              All test cases passed!
            </p>

            {/* Results Grid */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 mb-4 space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-gray-300">
                <span className="text-gray-700 font-medium text-sm">Status</span>
                <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full font-bold text-xs">
                  ✅ ACCEPTED
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium text-sm">Test Cases</span>
                <span className="text-gray-900 font-bold text-sm">
                  {submissionResults.passedCount}/{submissionResults.totalCount} passed
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium text-sm">Execution Time</span>
                <span className="text-gray-900 font-semibold text-sm">
                  ⏱️ {submissionResults.executionTime}ms
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium text-sm">Memory Used</span>
                <span className="text-gray-900 font-semibold text-sm">
                  💾 {submissionResults.memoryUsed}MB
                </span>
              </div>
            </div>

            {/* XP Award Section */}
            {submissionResults.xpAwarded && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 mb-4 border-2 border-purple-200">
                <div className="text-center mb-3">
                  <div className="text-3xl mb-1">⭐</div>
                  <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
                    XP AWARDED!
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium text-sm">Level</span>
                    <span className="text-purple-600 font-bold text-base">
                      {submissionResults.level}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium text-sm">Total XP</span>
                    <span className="text-purple-600 font-bold text-sm">
                      {submissionResults.experiencePoints}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium text-sm">XP to Next Level</span>
                    <span className="text-indigo-600 font-bold text-sm">
                      {submissionResults.xpToNextLevel || submissionResults.experienceToNextLevel}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="pt-1">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{Math.round(submissionResults.levelProgress || 0)}%</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 shadow-lg"
                        style={{ width: `${submissionResults.levelProgress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* XP Skipped Message - shown when solution was viewed */}
            {submissionResults.xpSkippedReason && (
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 mb-4 border-2 border-yellow-300">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">⚠️</span>
                  <div>
                    <h3 className="text-base font-bold text-yellow-800">
                      No XP Awarded
                    </h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      {submissionResults.xpSkippedReason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-bold text-base transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              Awesome! 🎉
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />

      {/* Mobile Warning Popup */}
      {showMobileWarning && isMobile && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
          onClick={() => {
            setShowMobileWarning(false);
            // Don't save to localStorage - show every time Question IDE opens
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-sm w-full shadow-2xl animate-scale-in"
            style={{ 
              padding: '24px',
              borderRadius: '20px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>💻</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
                Use Laptop for Best Experience
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                For the best coding experience with our IDE, we recommend using a laptop or desktop computer.
              </p>
            </div>
            <button
              onClick={() => {
                setShowMobileWarning(false);
                // Don't save to localStorage - show every time Question IDE opens
              }}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold text-base transition-all duration-200 shadow-lg"
              style={{
                minHeight: '48px'
              }}
            >
              Got it! 👍
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QuestionIDE;

